import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  PlusIcon,
  TrashIcon,
  PaintBrushIcon,
  PhotoIcon,
  DocumentArrowDownIcon,
  DocumentArrowUpIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  Cog6ToothIcon,
  EyeIcon,
  PencilIcon,
  LinkIcon,
  SparklesIcon,
  BookOpenIcon,
  LightBulbIcon,
  StarIcon,
  HeartIcon,
  FireIcon,
  BoltIcon,
  AcademicCapIcon,
  BeakerIcon,
  CalculatorIcon,
  GlobeAltIcon,
  LanguageIcon,
  MusicalNoteIcon,
  SwatchIcon as PaletteIcon,
  ChevronDownIcon,
  Square3Stack3DIcon,
  RocketLaunchIcon,
  SunIcon,
  MoonIcon,
  SwatchIcon,
  DocumentTextIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import {
  StarIcon as StarSolidIcon,
  HeartIcon as HeartSolidIcon,
  FireIcon as FireSolidIcon,
  BoltIcon as BoltSolidIcon,
  SunIcon as SunSolidIcon,
  MoonIcon as MoonSolidIcon
} from '@heroicons/react/24/solid'
import toast from 'react-hot-toast'
 import { useAuth } from '../hooks/useAuth'
 import mindMapService, { MindMap as FirebaseMindMap, MindMapNode as FirebaseMindMapNode } from '../services/mindMapService'
 import { useTheme } from '../hooks'
import { useKeyboardShortcuts, getMindMapKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'

// Types
interface MindMapNode {
  id: string
  text: string
  x: number
  y: number
  color: string
  icon?: string
  fontSize: number
  children: MindMapNode[]
  parentId?: string
  isExpanded: boolean
  notes?: string
  links?: { url: string; title: string }[]
  shape?: 'circle' | 'rectangle'
  fontWeight?: 'normal' | 'bold'
  shadow?: boolean
  gradient?: string
  borderWidth?: number
  borderColor?: string
  opacity?: number
  image?: string
}

interface MindMap {
  id: string
  title: string
  centerNode: MindMapNode
  theme: string
  createdAt: Date
  updatedAt: Date
  isPublic: boolean
  backgroundType?: 'solid' | 'dotted' | 'grid' | 'lines'
  backgroundColor?: string
  userId?: string
  pageSize?: 'A4' | 'A3' | 'Letter' | 'Custom'
  customWidth?: number
  customHeight?: number
}

// Sayfa boyutları (piksel cinsinden)
const pageSizes = {
  A4: { width: 794, height: 1123, name: 'A4 (210×297mm)' },
  A3: { width: 1123, height: 1587, name: 'A3 (297×420mm)' },
  Letter: { width: 816, height: 1056, name: 'Letter (8.5×11in)' },
  Custom: { width: 1200, height: 800, name: 'Özel Boyut' }
}

// Predefined themes
// Kapsamlı renk paleti - kategorilere ayrılmış
const colorPalette = {
  reds: ['#FF6B6B', '#FF5252', '#F44336', '#E53E3E', '#DC2626', '#B91C1C', '#991B1B', '#7F1D1D'],
  blues: ['#4ECDC4', '#42A5F5', '#2196F3', '#1976D2', '#1565C0', '#0D47A1', '#3B82F6', '#2563EB'],
  greens: ['#66BB6A', '#4CAF50', '#388E3C', '#2E7D32', '#1B5E20', '#10B981', '#059669', '#047857'],
  purples: ['#AB47BC', '#9C27B0', '#7B1FA2', '#6A1B9A', '#4A148C', '#8B5CF6', '#7C3AED', '#6D28D9'],
  oranges: ['#FF9800', '#F57C00', '#EF6C00', '#E65100', '#BF360C', '#F59E0B', '#D97706', '#B45309'],
  pinks: ['#EC407A', '#E91E63', '#C2185B', '#AD1457', '#880E4F', '#EC4899', '#DB2777', '#BE185D'],
  yellows: ['#FFEB3B', '#FDD835', '#F9A825', '#F57F17', '#FF8F00', '#FBBF24', '#F59E0B', '#D97706'],
  grays: ['#9E9E9E', '#757575', '#616161', '#424242', '#212121', '#6B7280', '#4B5563', '#374151'],
  teals: ['#26A69A', '#00897B', '#00695C', '#004D40', '#00251A', '#14B8A6', '#0D9488', '#0F766E'],
  indigos: ['#5C6BC0', '#3F51B5', '#303F9F', '#283593', '#1A237E', '#6366F1', '#4F46E5', '#4338CA']
}

const themes = {
  turkish: {
    name: 'Türkçe',
    colors: [...colorPalette.reds, ...colorPalette.pinks].slice(0, 12),
    gradients: ['linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'],
    background: 'bg-gradient-to-br from-red-50 to-pink-50 dark:from-gray-900 dark:to-red-900'
  },
  math: {
    name: 'Matematik',
    colors: [...colorPalette.blues, ...colorPalette.indigos].slice(0, 12),
    gradients: ['linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'],
    background: 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-blue-900'
  },
  science: {
    name: 'Fen Bilimleri',
    colors: [...colorPalette.greens, ...colorPalette.teals].slice(0, 12),
    gradients: ['linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)'],
    background: 'bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-900 dark:to-green-900'
  },
  revolution: {
    name: 'T.C. İnkılap Tarihi ve Atatürkçülük',
    colors: [...colorPalette.reds, ...colorPalette.oranges].slice(0, 12),
    gradients: ['linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)', 'linear-gradient(135deg, #ff9ff3 0%, #f368e0 100%)', 'linear-gradient(135deg, #feca57 0%, #ff9ff3 100%)'],
    background: 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-red-900'
  },
  english: {
    name: 'Yabancı Dil',
    colors: [...colorPalette.purples, ...colorPalette.indigos].slice(0, 12),
    gradients: ['linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'],
    background: 'bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-gray-900 dark:to-purple-900'
  },
  religion: {
    name: 'Din Kültürü ve Ahlâk Bilgisi',
    colors: [...colorPalette.teals, ...colorPalette.greens].slice(0, 12),
    gradients: ['linear-gradient(135deg, #48cae4 0%, #023047 100%)', 'linear-gradient(135deg, #06ffa5 0%, #3d5a80 100%)', 'linear-gradient(135deg, #a8e6cf 0%, #88d8c0 100%)'],
    background: 'bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-gray-900 dark:to-teal-900'
  }
}

// Arka plan seçenekleri
const backgroundOptions = {
  solid: {
    name: 'Düz Renk',
    colors: ['#FFFFFF', '#F8FAFC', '#F1F5F9', '#E2E8F0', '#CBD5E1', '#94A3B8']
  },
  dotted: {
    name: 'Noktalı',
    pattern: 'radial-gradient(circle, #CBD5E1 1px, transparent 1px)',
    size: '20px 20px'
  },
  grid: {
    name: 'Izgara',
    pattern: 'linear-gradient(#CBD5E1 1px, transparent 1px), linear-gradient(90deg, #CBD5E1 1px, transparent 1px)',
    size: '20px 20px'
  },
  lines: {
    name: 'Çizgiler',
    pattern: 'linear-gradient(90deg, transparent 24px, #CBD5E1 25px)',
    size: '25px 25px'
  }
}

// Available icons
const availableIcons = {
  '💡': LightBulbIcon,
  '⭐': StarIcon,
  '❤️': HeartIcon,
  '🔥': FireIcon,
  '⚡': BoltIcon,
  '🎓': AcademicCapIcon,
  '🧪': BeakerIcon,
  '🔢': CalculatorIcon,
  '🌍': GlobeAltIcon,
  '🗣️': LanguageIcon,
  '🎵': MusicalNoteIcon,
  '🎨': PaletteIcon,
  '🚀': RocketLaunchIcon,
  '📚': BookOpenIcon,
  '✨': SparklesIcon
}

const MindMaps: React.FC = () => {
  const { user, loading: authLoading } = useAuth()
  const [mindMaps, setMindMaps] = useState<MindMap[]>([])
  const [loading, setLoading] = useState(true)
  const [currentMap, setCurrentMap] = useState<MindMap | null>(null)
  const [selectedNode, setSelectedNode] = useState<MindMapNode | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [newMapTitle, setNewMapTitle] = useState('')
  const [selectedTheme, setSelectedTheme] = useState('default')
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [showNodeEditor, setShowNodeEditor] = useState(false)
  const [editingNode, setEditingNode] = useState<MindMapNode | null>(null)
  const [nodeText, setNodeText] = useState('')
  const [nodeColor, setNodeColor] = useState('#8B5CF6')
  const [nodeIcon, setNodeIcon] = useState('')
  const [nodeFontSize, setNodeFontSize] = useState(14)
  const [nodeNotes, setNodeNotes] = useState('')
  const [nodeShape, setNodeShape] = useState<'circle' | 'rectangle'>('rectangle')
  const [nodeFontWeight, setNodeFontWeight] = useState<'normal' | 'bold'>('normal')
  const [nodeShadow, setNodeShadow] = useState(false)
  const [nodeGradient, setNodeGradient] = useState('')
  const [isDraggingNode, setIsDraggingNode] = useState(false)
  const [draggedNode, setDraggedNode] = useState<MindMapNode | null>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [showThemeSelector, setShowThemeSelector] = useState(false)
  const [showExportOptions, setShowExportOptions] = useState(false)
  const [exportTransparent, setExportTransparent] = useState(false)
  const [exportFormat, setExportFormat] = useState<'A4' | 'A3' | 'Letter'>('A4')
  const [exportQuality, setExportQuality] = useState<1 | 2 | 3>(2)
  
  // Sayfa boyutu kontrolü için state'ler
  const [pageSize, setPageSize] = useState<'A4' | 'A3' | 'Letter' | 'Custom'>('A4')
  const [customPageWidth, setCustomPageWidth] = useState(1200)
  const [customPageHeight, setCustomPageHeight] = useState(800)
  const [showPageBounds, setShowPageBounds] = useState(true)
  const [showCustomSizeModal, setShowCustomSizeModal] = useState(false)
  const [prevPageSize, setPrevPageSize] = useState<'A4' | 'A3' | 'Letter' | 'Custom'>('A4')
  
  // Kapsamlı renk ve arka plan seçenekleri için state'ler
  const [selectedColorCategory, setSelectedColorCategory] = useState<keyof typeof colorPalette>('blues')
  const [customNodeColor, setCustomNodeColor] = useState('')
  const [nodeBorderWidth, setNodeBorderWidth] = useState(2)
  const [nodeBorderColor, setNodeBorderColor] = useState('#000000')
  const [nodeOpacity, setNodeOpacity] = useState(1)
  const [nodeLinks, setNodeLinks] = useState<{url: string, title: string}[]>([])
  const [newLinkUrl, setNewLinkUrl] = useState('')
  const [newLinkTitle, setNewLinkTitle] = useState('')
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false)
  
  // Kaydetme durumu için state'ler
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true)
const [showPageSizePicker, setShowPageSizePicker] = useState(false)

  // Undo/redo & clipboard state
  const [history, setHistory] = useState<MindMap[]>([])
  const [future, setFuture] = useState<MindMap[]>([])
  const [clipboardNode, setClipboardNode] = useState<MindMapNode | null>(null)
  const lastSnapshotMapIdRef = useRef<string | null>(null)
  
  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false)
  
  // Tam ekran durumu ve değiştirici
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])
  
  const toggleFullscreen = async () => {
     try {
       if (!document.fullscreenElement) {
         const el = containerRef.current || canvasRef.current
         if (el && el.requestFullscreen) {
           await el.requestFullscreen()
         }
       } else {
         if (document.exitFullscreen) await document.exitFullscreen()
       }
     } catch (e) {
       console.error('Tam ekran değiştirilemedi:', e)
       toast.error('Tam ekran moduna geçişte bir sorun oluştu')
     }
   }
   
   // Otomatik tam ekran devre dışı - sadece manuel tam ekran
     // useEffect(() => {
     //   const isMobileOrTablet = window.innerWidth <= 1024 // Tablet ve mobil için
     //   
     //   if (currentMap && !document.fullscreenElement && isMobileOrTablet) {
     //     const timer = setTimeout(async () => {
     //       try {
     //         const el = containerRef.current || canvasRef.current
     //         if (el && el.requestFullscreen) {
     //           await el.requestFullscreen()
     //         }
     //       } catch (e) {
     //         console.error('Otomatik tam ekran başarısız:', e)
     //       }
     //     }, 500) // 500ms gecikme ile
     //     
     //     return () => clearTimeout(timer)
     //   }
     // }, [currentMap])
    
    // Geri tuşu ile tam ekrandan çıkma
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && document.fullscreenElement) {
          if (document.exitFullscreen) {
            document.exitFullscreen()
          }
        }
      }
      
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }, [])
  
  const canvasRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const autoFsTriedForMapIdRef = useRef<string | null>(null)

  // Tema bilgisi (karanlık/açık)
  const { isDark, sidebarOpen } = useTheme()

  // Snapshot helper functions for undo/redo
  const takeSnapshot = useCallback((map: MindMap) => {
    if (!map || lastSnapshotMapIdRef.current === map.id) return
    
    setHistory(prev => {
      const newHistory = [...prev, { ...map }]
      // Keep only last 20 snapshots to prevent memory issues
      return newHistory.slice(-20)
    })
    setFuture([])
    lastSnapshotMapIdRef.current = map.id
  }, [])

  const undo = useCallback(() => {
    if (history.length === 0 || !currentMap) return
    
    const previousState = history[history.length - 1]
    setFuture(prev => [currentMap, ...prev])
    setHistory(prev => prev.slice(0, -1))
    setCurrentMap(previousState)
    setMindMaps(prev => prev.map(map => map.id === previousState.id ? previousState : map))
    lastSnapshotMapIdRef.current = previousState.id
  }, [history, currentMap])

  const redo = useCallback(() => {
    if (future.length === 0) return
    
    const nextState = future[0]
    if (currentMap) {
      setHistory(prev => [...prev, currentMap])
    }
    setFuture(prev => prev.slice(1))
    setCurrentMap(nextState)
    setMindMaps(prev => prev.map(map => map.id === nextState.id ? nextState : map))
    lastSnapshotMapIdRef.current = nextState.id
  }, [future, currentMap])

  // Helper function to expand/collapse all nodes
  const toggleAllNodes = useCallback((expand: boolean) => {
    if (!currentMap) return
    
    const updateNodeExpansion = (node: MindMapNode): MindMapNode => ({
      ...node,
      isExpanded: expand,
      children: node.children.map(updateNodeExpansion)
    })
    
    takeSnapshot(currentMap)
    const updatedMap = {
      ...currentMap,
      centerNode: updateNodeExpansion(currentMap.centerNode),
      updatedAt: new Date()
    }
    setCurrentMap(updatedMap)
    setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
  }, [currentMap, takeSnapshot])

  // Helper function to toggle grid background
  const toggleGrid = useCallback(() => {
    if (!currentMap) return
    
    takeSnapshot(currentMap)
    const newBackgroundType = currentMap.backgroundType === 'grid' ? 'solid' : 'grid'
    const updatedMap = {
      ...currentMap,
      backgroundType: newBackgroundType,
      backgroundColor: newBackgroundType === 'solid' ? '#FFFFFF' : currentMap.backgroundColor,
      updatedAt: new Date()
    }
    setCurrentMap(updatedMap)
    setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
    toast.success(`Izgara ${newBackgroundType === 'grid' ? 'açıldı' : 'kapatıldı'}`)
  }, [currentMap, takeSnapshot])

  // Helper function to copy selected node
  const copyNode = useCallback(() => {
    if (!selectedNode) {
      toast.error('Kopyalamak için bir düğüm seçin')
      return
    }
    setClipboardNode(selectedNode)
    toast.success('Düğüm kopyalandı')
  }, [selectedNode])

  // Helper function to paste copied node
  const pasteNode = useCallback(() => {
    if (!clipboardNode || !selectedNode || !currentMap) {
      toast.error('Yapıştırmak için önce bir düğüm kopyalayın ve hedef düğümü seçin')
      return
    }
    
    // Take snapshot before making changes
    takeSnapshot(currentMap)
    
    try {
      const newNode: MindMapNode = {
        ...clipboardNode,
        id: Date.now().toString(),
        x: selectedNode.x + 100,
        y: selectedNode.y + 50,
        parentId: selectedNode.id,
        children: []
      }
      
      const updateNodeInTree = (node: MindMapNode): MindMapNode => {
        if (node.id === selectedNode.id) {
          return { ...node, children: [...node.children, newNode] }
        }
        return { ...node, children: node.children.map(updateNodeInTree) }
      }
      
      const updatedMap = {
        ...currentMap,
        centerNode: updateNodeInTree(currentMap.centerNode),
        updatedAt: new Date()
      }
      
      setCurrentMap(updatedMap)
      setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
      setSelectedNode(newNode)
      
      toast.success('Düğüm yapıştırıldı')
    } catch (error) {
      console.error('Düğüm yapıştırılamadı:', error)
      toast.error('Düğüm yapıştırılamadı')
    }
  }, [clipboardNode, selectedNode, currentMap, takeSnapshot, setCurrentMap, setMindMaps, setSelectedNode])

  // Keyboard shortcut registration moved below zoom controls to avoid TDZ

  // Firebase'den zihin haritalarını yükle
  useEffect(() => {
    const loadMindMaps = async () => {
      // Auth yükleniyor, bekle
      if (authLoading) {
        return
      }
      
      // Kullanıcı giriş yapmamış
      if (!user) {
        setMindMaps([])
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const userMindMaps = await mindMapService.getUserMindMaps()
        setMindMaps(userMindMaps)
      } catch (error) {
        console.error('Zihin haritaları yüklenemedi:', error)
        toast.error('Zihin haritaları yüklenemedi')
        setMindMaps([]) // Hata durumunda boş array
      } finally {
        setLoading(false)
      }
    }

    // Sadece auth loading tamamlandığında çalıştır
    if (!authLoading) {
      loadMindMaps()
    }

    // Cleanup function
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [user, authLoading])


  // currentMap değiştiğinde sayfa boyutu ve özel boyutları senkronize et
  useEffect(() => {
    if (!currentMap) {
      setPageSize('A4')
      setCustomPageWidth(pageSizes.A4.width)
      setCustomPageHeight(pageSizes.A4.height)
      return
    }

    const mapPageSize = (currentMap.pageSize ?? 'A4') as 'A4' | 'A3' | 'Letter' | 'Custom'
    setPageSize(mapPageSize)

    if (mapPageSize === 'Custom') {
      setCustomPageWidth(currentMap.customWidth ?? pageSizes.Custom.width)
      setCustomPageHeight(currentMap.customHeight ?? pageSizes.Custom.height)
    } else {
      setCustomPageWidth(pageSizes[mapPageSize].width)
      setCustomPageHeight(pageSizes[mapPageSize].height)
    }
  }, [currentMap])

  // Otomatik tam ekran devre dışı: currentMap değiştiğinde otomatik tam ekrana geçilmeyecek.

  // Dokunmatik cihazlarda (telefon/tablet) harita açıldığında otomatik tam ekran (tek seferlik + etkileşim tabanlı yedek)
   useEffect(() => {
     const isTouch = (('ontouchstart' in window) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches))
     const isMobileOrTablet = window.innerWidth <= 1024
 
     const el = containerRef.current || canvasRef.current
     const attemptFs = async () => {
       try {
         if (!document.fullscreenElement && el) {
           const anyEl: any = el
           if (el.requestFullscreen) {
             await el.requestFullscreen()
           } else if (anyEl.webkitRequestFullscreen) {
             await anyEl.webkitRequestFullscreen()
           } else if (anyEl.msRequestFullscreen) {
             await anyEl.msRequestFullscreen()
           }
         }
       } catch (e) {
         console.error('Mobil otomatik tam ekran başarısız:', e)
       }
     }
 
     if (currentMap && isTouch && isMobileOrTablet && !document.fullscreenElement) {
       if (autoFsTriedForMapIdRef.current !== currentMap.id) {
         autoFsTriedForMapIdRef.current = currentMap.id
         // Render sonrasına kısa gecikmeli ilk deneme (bazı tarayıcılarda jestersiz reddedilebilir)
         setTimeout(() => {
           attemptFs()
         }, 200)
       }
     }
 
     // Kullanıcı etkileşimi ile yedek: ilk dokunuş/tıklamada tam ekrana geçmeyi dene
     const handleFirstInteract = () => {
       if (!document.fullscreenElement && currentMap) {
         attemptFs()
       }
       if (el) {
         el.removeEventListener('pointerdown', handleFirstInteract)
         el.removeEventListener('touchend', handleFirstInteract)
         el.removeEventListener('click', handleFirstInteract)
       }
     }
 
     if (currentMap && isTouch && isMobileOrTablet && el) {
       el.addEventListener('pointerdown', handleFirstInteract, { once: true } as any)
       el.addEventListener('touchend', handleFirstInteract, { once: true } as any)
       el.addEventListener('click', handleFirstInteract, { once: true } as any)
     }
 
     return () => {
       if (el) {
         el.removeEventListener('pointerdown', handleFirstInteract)
         el.removeEventListener('touchend', handleFirstInteract)
         el.removeEventListener('click', handleFirstInteract)
       }
       if (!currentMap) {
         autoFsTriedForMapIdRef.current = null
       }
     }
   }, [currentMap])
  
   // currentMap kapandığında otomatik tam ekrandan çık
   useEffect(() => {
     if (!currentMap && document.fullscreenElement) {
       try {
         document.exitFullscreen()
       } catch (e) {
         console.error('Tam ekrandan çıkış (currentMap=null) hatası:', e)
       }
     }
   }, [currentMap])

  const handlePageSizeChange = (newSize: 'A4' | 'A3' | 'Letter' | 'Custom') => {
    if (newSize === 'Custom') {
      setPrevPageSize(pageSize)
      setShowCustomSizeModal(true)
      return
    }

    setPageSize(newSize)
    setCustomPageWidth(pageSizes[newSize].width)
    setCustomPageHeight(pageSizes[newSize].height)

    if (currentMap) {
      takeSnapshot(currentMap)
      const updatedMap = {
        ...currentMap,
        pageSize: newSize,
        customWidth: pageSizes[newSize].width,
        customHeight: pageSizes[newSize].height,
        updatedAt: new Date()
      }
      setCurrentMap(updatedMap)
      triggerAutoSave(updatedMap)
    }
  }

  // LGS Template Data
  const lgsTemplates = {
    'matematik-cebirsel': {
      title: 'Matematik - Cebirsel İfadeler',
      theme: 'math',
      centerNode: {
        id: 'center',
        text: 'Cebirsel İfadeler',
        x: 400,
        y: 300,
        color: '#3B82F6',
        fontSize: 18,
        children: [
          {
            id: 'terimler',
            text: 'Terimler',
            x: 250,
            y: 200,
            color: '#1976D2',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'sabit-terim',
                text: 'Sabit Terim',
                x: 150,
                y: 150,
                color: '#0D47A1',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Sayı olan terimler. Örnek: 5, -3, 7'
              },
              {
                id: 'degisken-terim',
                text: 'Değişken Terim',
                x: 150,
                y: 250,
                color: '#0D47A1',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Harf içeren terimler. Örnek: 3x, -2y, 5ab'
              }
            ],
            isExpanded: true,
            notes: 'Cebirsel ifadelerin yapı taşları'
          },
          {
            id: 'islemler',
            text: 'İşlemler',
            x: 550,
            y: 200,
            color: '#1976D2',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'toplama-cikarma',
                text: 'Toplama-Çıkarma',
                x: 650,
                y: 150,
                color: '#0D47A1',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Benzer terimler toplanır/çıkarılır'
              },
              {
                id: 'carpma-bolme',
                text: 'Çarpma-Bölme',
                x: 650,
                y: 250,
                color: '#0D47A1',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Katsayılar ve üsler ayrı işleme alınır'
              }
            ],
            isExpanded: true,
            notes: 'Cebirsel ifadelerle yapılan temel işlemler'
          },
          {
            id: 'carpanlara-ayirma',
            text: 'Çarpanlara Ayırma',
            x: 400,
            y: 450,
            color: '#1976D2',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'ortak-carpan',
                text: 'Ortak Çarpan',
                x: 300,
                y: 500,
                color: '#0D47A1',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'ax + ay = a(x + y)'
              },
              {
                id: 'ozdeslikler',
                text: 'Özdeşlikler',
                x: 500,
                y: 500,
                color: '#0D47A1',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'a² - b² = (a+b)(a-b)'
              }
            ],
            isExpanded: true,
            notes: 'İfadeleri çarpım şeklinde yazma'
          }
        ],
        isExpanded: true,
        shape: 'rectangle',
        fontWeight: 'bold',
        shadow: true
      }
    },
    'fen-asitler-bazlar': {
      title: 'Fen Bilimleri - Asitler ve Bazlar',
      theme: 'science',
      centerNode: {
        id: 'center',
        text: 'Asitler ve Bazlar',
        x: 400,
        y: 300,
        color: '#4CAF50',
        fontSize: 18,
        children: [
          {
            id: 'asitler',
            text: 'Asitler',
            x: 250,
            y: 200,
            color: '#388E3C',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'asit-ozellikleri',
                text: 'Özellikler',
                x: 150,
                y: 150,
                color: '#2E7D32',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Ekşi tat, pH < 7, H+ iyonu verir'
              },
              {
                id: 'asit-ornekleri',
                text: 'Örnekler',
                x: 150,
                y: 250,
                color: '#2E7D32',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'HCl, H₂SO₄, HNO₃, sirke asidi'
              }
            ],
            isExpanded: true,
            notes: 'Hidrojen iyonu (H+) veren maddeler'
          },
          {
            id: 'bazlar',
            text: 'Bazlar',
            x: 550,
            y: 200,
            color: '#388E3C',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'baz-ozellikleri',
                text: 'Özellikler',
                x: 650,
                y: 150,
                color: '#2E7D32',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Acı tat, pH > 7, OH- iyonu verir'
              },
              {
                id: 'baz-ornekleri',
                text: 'Örnekler',
                x: 650,
                y: 250,
                color: '#2E7D32',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'NaOH, KOH, Ca(OH)₂, amonyak'
              }
            ],
            isExpanded: true,
            notes: 'Hidroksit iyonu (OH-) veren maddeler'
          },
          {
            id: 'ph-skala',
            text: 'pH Skalası',
            x: 400,
            y: 450,
            color: '#388E3C',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'asidik',
                text: 'Asidik (0-7)',
                x: 300,
                y: 500,
                color: '#2E7D32',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Kırmızı litmus kağıdı'
              },
              {
                id: 'bazik',
                text: 'Bazik (7-14)',
                x: 500,
                y: 500,
                color: '#2E7D32',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Mavi litmus kağıdı'
              }
            ],
            isExpanded: true,
            notes: 'Asitlik-bazlık ölçeği (0-14)'
          }
        ],
        isExpanded: true,
        shape: 'rectangle',
        fontWeight: 'bold',
        shadow: true
      }
    },
    'turkce-sozcuk-anlam': {
      title: 'Türkçe - Sözcükte Anlam',
      theme: 'turkish',
      centerNode: {
        id: 'center',
        text: 'Sözcükte Anlam',
        x: 400,
        y: 300,
        color: '#F44336',
        fontSize: 18,
        children: [
          {
            id: 'gercek-anlam',
            text: 'Gerçek Anlam',
            x: 250,
            y: 200,
            color: '#E53E3E',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'temel-anlam',
                text: 'Temel Anlam',
                x: 150,
                y: 150,
                color: '#DC2626',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Sözcüğün sözlükteki ilk anlamı'
              },
              {
                id: 'somut-anlam',
                text: 'Somut Anlam',
                x: 150,
                y: 250,
                color: '#DC2626',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Elle tutulur, gözle görülür anlamlar'
              }
            ],
            isExpanded: true,
            notes: 'Sözcüğün asıl, doğrudan anlamı'
          },
          {
            id: 'mecaz-anlam',
            text: 'Mecaz Anlam',
            x: 550,
            y: 200,
            color: '#E53E3E',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'benzetme',
                text: 'Benzetme',
                x: 650,
                y: 150,
                color: '#DC2626',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Benzerlik kurarak kullanım'
              },
              {
                id: 'aktarma',
                text: 'Aktarma',
                x: 650,
                y: 250,
                color: '#DC2626',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Bir alandan başka alana aktarım'
              }
            ],
            isExpanded: true,
            notes: 'Sözcüğün yan, dolaylı anlamı'
          },
          {
            id: 'anlam-iliskileri',
            text: 'Anlam İlişkileri',
            x: 400,
            y: 450,
            color: '#E53E3E',
            fontSize: 14,
            shape: 'rectangle',
            children: [
              {
                id: 'es-anlam',
                text: 'Eş Anlam',
                x: 250,
                y: 500,
                color: '#DC2626',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Aynı anlama gelen sözcükler'
              },
              {
                id: 'zit-anlam',
                text: 'Zıt Anlam',
                x: 400,
                y: 550,
                color: '#DC2626',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Karşıt anlama gelen sözcükler'
              },
              {
                id: 'es-sesli',
                text: 'Eş Sesli',
                x: 550,
                y: 500,
                color: '#DC2626',
                fontSize: 12,
                shape: 'rectangle',
                children: [],
                isExpanded: true,
                notes: 'Aynı sesli, farklı anlamlı sözcükler'
              }
            ],
            isExpanded: true,
            notes: 'Sözcükler arası anlam bağlantıları'
          }
        ],
        isExpanded: true,
        shape: 'rectangle',
        fontWeight: 'bold',
        shadow: true
      }
     },
     'tarih-osmanli': {
       title: 'Tarih - Osmanlı Devleti',
       theme: 'history',
       centerNode: {
         id: 'center',
         text: 'Osmanlı Devleti',
         x: 400,
         y: 300,
         color: '#FF9800',
         fontSize: 18,
         children: [
           {
             id: 'kuruluş',
             text: 'Kuruluş Dönemi',
             x: 250,
             y: 200,
             color: '#F57C00',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'osman-bey',
                 text: 'Osman Bey',
                 x: 150,
                 y: 150,
                 color: '#EF6C00',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: '1299-1326, Osmanlı Devleti\'nin kurucusu'
               },
               {
                 id: 'orhan-bey',
                 text: 'Orhan Bey',
                 x: 150,
                 y: 250,
                 color: '#EF6C00',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: '1326-1362, İlk para bastırdı, Bursa\'yı aldı'
               }
             ],
             isExpanded: true,
             notes: '1299-1453 arası dönem'
           },
           {
             id: 'yükselme',
             text: 'Yükselme Dönemi',
             x: 550,
             y: 200,
             color: '#F57C00',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'fatih-sultan-mehmet',
                 text: 'Fatih Sultan Mehmet',
                 x: 650,
                 y: 150,
                 color: '#EF6C00',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: '1453 İstanbul\'un fethi'
               },
               {
                 id: 'kanuni-sultan-süleyman',
                 text: 'Kanuni Sultan Süleyman',
                 x: 650,
                 y: 250,
                 color: '#EF6C00',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: '1520-1566, Altın çağ, Kanunname'
               }
             ],
             isExpanded: true,
             notes: '1453-1683 arası dönem'
           },
           {
             id: 'duraklama-gerileme',
             text: 'Duraklama ve Gerileme',
             x: 400,
             y: 450,
             color: '#F57C00',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'viyana-kuşatması',
                 text: 'II. Viyana Kuşatması',
                 x: 300,
                 y: 500,
                 color: '#EF6C00',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: '1683, Başarısızlık'
               },
               {
                 id: 'karlofça-antlaşması',
                 text: 'Karlofça Antlaşması',
                 x: 500,
                 y: 500,
                 color: '#EF6C00',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: '1699, İlk toprak kaybı'
               }
             ],
             isExpanded: true,
             notes: '1683-1792 arası dönem'
           }
         ],
         isExpanded: true,
         shape: 'rectangle',
         fontWeight: 'bold',
         shadow: true
       }
     },
     'cografya-iklim': {
       title: 'Coğrafya - İklim Tipleri',
       theme: 'geography',
       centerNode: {
         id: 'center',
         text: 'İklim Tipleri',
         x: 400,
         y: 300,
         color: '#4CAF50',
         fontSize: 18,
         children: [
           {
             id: 'karasal-iklim',
             text: 'Karasal İklim',
             x: 250,
             y: 200,
             color: '#388E3C',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'karasal-özellikler',
                 text: 'Özellikler',
                 x: 150,
                 y: 150,
                 color: '#2E7D32',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Sıcak yaz, soğuk kış, az yağış'
               },
               {
                 id: 'karasal-bölgeler',
                 text: 'Görüldüğü Yerler',
                 x: 150,
                 y: 250,
                 color: '#2E7D32',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'İç Anadolu, Doğu Anadolu'
               }
             ],
             isExpanded: true,
             notes: 'Kıtaların iç kesimlerinde görülür'
           },
           {
             id: 'akdeniz-iklimi',
             text: 'Akdeniz İklimi',
             x: 550,
             y: 200,
             color: '#388E3C',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'akdeniz-özellikler',
                 text: 'Özellikler',
                 x: 650,
                 y: 150,
                 color: '#2E7D32',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Sıcak-kurak yaz, ılık-yağışlı kış'
               },
               {
                 id: 'akdeniz-bölgeler',
                 text: 'Görüldüğü Yerler',
                 x: 650,
                 y: 250,
                 color: '#2E7D32',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Akdeniz, Ege kıyıları'
               }
             ],
             isExpanded: true,
             notes: 'Subtropikal kuşakta görülür'
           },
           {
             id: 'karadeniz-iklimi',
             text: 'Karadeniz İklimi',
             x: 400,
             y: 450,
             color: '#388E3C',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'karadeniz-özellikler',
                 text: 'Özellikler',
                 x: 300,
                 y: 500,
                 color: '#2E7D32',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Her mevsim yağışlı, ılıman'
               },
               {
                 id: 'karadeniz-bölgeler',
                 text: 'Görüldüğü Yerler',
                 x: 500,
                 y: 500,
                 color: '#2E7D32',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Karadeniz kıyıları'
               }
             ],
             isExpanded: true,
             notes: 'Okyanus iklimi türü'
           }
         ],
         isExpanded: true,
         shape: 'rectangle',
         fontWeight: 'bold',
         shadow: true
       }
     },
     'ingilizce-present-tense': {
       title: 'İngilizce - Present Tense',
       theme: 'english',
       centerNode: {
         id: 'center',
         text: 'Present Tense',
         x: 400,
         y: 300,
         color: '#9C27B0',
         fontSize: 18,
         children: [
           {
             id: 'simple-present',
             text: 'Simple Present',
             x: 250,
             y: 200,
             color: '#7B1FA2',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'simple-present-yapı',
                 text: 'Yapı',
                 x: 150,
                 y: 150,
                 color: '#6A1B9A',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'I/You/We/They + V1\nHe/She/It + V1+s/es'
               },
               {
                 id: 'simple-present-kullanım',
                 text: 'Kullanım',
                 x: 150,
                 y: 250,
                 color: '#6A1B9A',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Alışkanlıklar, genel gerçekler'
               }
             ],
             isExpanded: true,
             notes: 'Geniş zaman'
           },
           {
             id: 'present-continuous',
             text: 'Present Continuous',
             x: 550,
             y: 200,
             color: '#7B1FA2',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'continuous-yapı',
                 text: 'Yapı',
                 x: 650,
                 y: 150,
                 color: '#6A1B9A',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'am/is/are + V1+ing'
               },
               {
                 id: 'continuous-kullanım',
                 text: 'Kullanım',
                 x: 650,
                 y: 250,
                 color: '#6A1B9A',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Şu anda olan eylemler'
               }
             ],
             isExpanded: true,
             notes: 'Şimdiki zaman'
           },
           {
             id: 'present-perfect',
             text: 'Present Perfect',
             x: 400,
             y: 450,
             color: '#7B1FA2',
             fontSize: 14,
             shape: 'rectangle',
             children: [
               {
                 id: 'perfect-yapı',
                 text: 'Yapı',
                 x: 300,
                 y: 500,
                 color: '#6A1B9A',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'have/has + V3'
               },
               {
                 id: 'perfect-kullanım',
                 text: 'Kullanım',
                 x: 500,
                 y: 500,
                 color: '#6A1B9A',
                 fontSize: 12,
                 shape: 'rectangle',
                 children: [],
                 isExpanded: true,
                 notes: 'Geçmişte başlayıp şimdi etkisi olan'
               }
             ],
             isExpanded: true,
             notes: 'Şimdiki zamanın hikayesi'
           }
         ],
         isExpanded: true,
         shape: 'rectangle',
         fontWeight: 'bold',
         shadow: true
       }
     }
   }

  // Create template mind map
  const createTemplateMap = useCallback(async (templateKey: string) => {
    if (!user) {
      toast.error('Zihin haritası oluşturmak için giriş yapmalısınız')
      return
    }

    const template = lgsTemplates[templateKey as keyof typeof lgsTemplates]
    if (!template) return
    
    try {
      const newMapData = {
        title: template.title,
        centerNode: template.centerNode as MindMapNode,
        theme: template.theme,
        isPublic: false,
        backgroundType: 'solid' as const,
        backgroundColor: isDark ? '#0f172a' : '#FFFFFF',
        pageSize,
        customWidth: pageSize === 'Custom' ? customPageWidth : undefined,
        customHeight: pageSize === 'Custom' ? customPageHeight : undefined
      }
      
      const newMapId = await mindMapService.createMindMap(newMapData)
      const newMap: MindMap = {
        id: newMapId,
        ...newMapData,
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: user.uid
      }
      
      setMindMaps(prev => [...prev, newMap])
      setCurrentMap(newMap)
      setIsCreating(false)
      setNewMapTitle('')
      setSelectedTheme('default')
      toast.success('Şablon harita oluşturuldu!')
    } catch (error) {
      console.error('Şablon harita oluşturulamadı:', error)
      toast.error('Şablon harita oluşturulamadı')
    }
  }, [user])

  // Create new mind map
  const createNewMindMap = useCallback(async () => {
    if (!newMapTitle.trim()) return
    
    if (!user) {
      toast.error('Zihin haritası oluşturmak için giriş yapmalısınız')
      return
    }
    
    try {
      const centerNode: MindMapNode = {
        id: 'center',
        text: newMapTitle,
        x: 400,
        y: 300,
        color: themes[selectedTheme as keyof typeof themes]?.colors[0] || '#8B5CF6',
        fontSize: 18,
        children: [],
        isExpanded: true,
        shape: 'rectangle',
        fontWeight: 'bold',
        shadow: true
      }
      
      const newMapData = {
        title: newMapTitle,
        centerNode,
        theme: selectedTheme,
        isPublic: false,
        backgroundType: 'solid' as const,
        backgroundColor: isDark ? '#0f172a' : '#FFFFFF',
        pageSize,
        customWidth: pageSize === 'Custom' ? customPageWidth : undefined,
        customHeight: pageSize === 'Custom' ? customPageHeight : undefined
      }
      
      const newMapId = await mindMapService.createMindMap(newMapData)
      const newMap: MindMap = {
        id: newMapId,
        ...newMapData,
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: user.uid
      }
      
      setMindMaps(prev => [...prev, newMap])
      setCurrentMap(newMap)
      setIsCreating(false)
      setNewMapTitle('')
      setSelectedTheme('default')
      toast.success('Yeni harita oluşturuldu!')
    } catch (error) {
      console.error('Yeni harita oluşturulamadı:', error)
      toast.error('Yeni harita oluşturulamadı')
    }
  }, [newMapTitle, selectedTheme, user])

  // Auto-save function
  const autoSave = useCallback(async (mapToSave: MindMap) => {
    if (!autoSaveEnabled || !user || !mapToSave) return
    
    try {
      setIsSaving(true)
      await mindMapService.updateMindMap(mapToSave.id, {
        centerNode: mapToSave.centerNode,
        title: mapToSave.title,
        theme: mapToSave.theme,
        backgroundType: mapToSave.backgroundType,
        backgroundColor: mapToSave.backgroundColor,
        isPublic: mapToSave.isPublic,
        pageSize: mapToSave.pageSize,
        customWidth: mapToSave.customWidth,
        customHeight: mapToSave.customHeight
      })
      setLastSaved(new Date())
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Auto-save hatası:', error)
    } finally {
      setIsSaving(false)
    }
  }, [autoSaveEnabled, user])

  // Manual save function
  const manualSave = useCallback(async () => {
    if (!currentMap || !user) return
    
    try {
      setIsSaving(true)
      await mindMapService.updateMindMap(currentMap.id, {
        centerNode: currentMap.centerNode,
        title: currentMap.title,
        theme: currentMap.theme,
        backgroundType: currentMap.backgroundType,
        backgroundColor: currentMap.backgroundColor,
        isPublic: currentMap.isPublic,
        pageSize: currentMap.pageSize,
        customWidth: currentMap.customWidth,
        customHeight: currentMap.customHeight
      })
      setLastSaved(new Date())
      setHasUnsavedChanges(false)
      toast.success('Zihin haritası kaydedildi!')
    } catch (error) {
      console.error('Kaydetme hatası:', error)
      toast.error('Kaydetme işlemi başarısız oldu')
    } finally {
      setIsSaving(false)
    }
  }, [currentMap, user])

  // Trigger auto-save with debounce
  const triggerAutoSave = useCallback((mapToSave: MindMap) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }
    
    setHasUnsavedChanges(true)
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave(mapToSave)
    }, 2000) // 2 saniye bekle
  }, [autoSave])

  // Add new node
  const addNode = useCallback(async (parentNode: MindMapNode, angle?: number) => {
    if (!currentMap) return
    
    // Take snapshot before making changes
    takeSnapshot(currentMap)
    
    try {
      const colors = themes[currentMap.theme as keyof typeof themes]?.colors || themes.turkish.colors
      const randomColor = colors[Math.floor(Math.random() * colors.length)]
      
      const newNode: MindMapNode = {
        id: Date.now().toString(),
        text: 'Yeni Dal',
        x: parentNode.x + (Math.cos((angle || 0) * Math.PI / 180) * 120),
        y: parentNode.y + (Math.sin((angle || 0) * Math.PI / 180) * 120),
        color: randomColor,
        fontSize: Math.max(12, parentNode.fontSize - 2),
        children: [],
        parentId: parentNode.id,
        isExpanded: true,
        shape: 'rectangle',
        fontWeight: 'normal',
        shadow: false
      }
      
      const updateNodeInTree = (node: MindMapNode): MindMapNode => {
        if (node.id === parentNode.id) {
          return { ...node, children: [...node.children, newNode] }
        }
        return { ...node, children: node.children.map(updateNodeInTree) }
      }
      
      const updatedMap = {
        ...currentMap,
        centerNode: updateNodeInTree(currentMap.centerNode),
        updatedAt: new Date()
      }
      
      setCurrentMap(updatedMap)
      setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
      setSelectedNode(newNode)
      
      // Auto-save tetikle
      triggerAutoSave(updatedMap)
      
      toast.success('Yeni dal eklendi!')
    } catch (error) {
      console.error('Yeni dal eklenemedi:', error)
      toast.error('Yeni dal eklenemedi')
    }
  }, [currentMap])

  // Edit node
  const editNode = useCallback((node: MindMapNode) => {
    setEditingNode(node)
    setNodeText(node.text)
    setNodeColor(node.color)
    setNodeIcon(node.icon || '')
    setNodeFontSize(node.fontSize)
    setNodeNotes(node.notes || '')
    setNodeShape(node.shape || 'rectangle')
    setNodeFontWeight(node.fontWeight || 'normal')
    setNodeShadow(node.shadow || false)
    setNodeGradient(node.gradient || '')
    setNodeBorderWidth(node.borderWidth || 2)
    setNodeBorderColor(node.borderColor || '#000000')
    setNodeOpacity(node.opacity || 1)
    setNodeLinks(node.links || [])
    setShowNodeEditor(true)
  }, [])

  // Save node changes
  const saveNodeChanges = useCallback(async () => {
    if (!editingNode || !currentMap) return
    
    // Take snapshot before making changes
    takeSnapshot(currentMap)
    
    try {
      const updatedNode = {
        ...editingNode,
        text: nodeText,
        color: nodeColor,
        icon: nodeIcon,
        fontSize: nodeFontSize,
        notes: nodeNotes,
        shape: nodeShape,
        fontWeight: nodeFontWeight,
        shadow: nodeShadow,
        gradient: nodeGradient,
        borderWidth: nodeBorderWidth,
        borderColor: nodeBorderColor,
        opacity: nodeOpacity,
        links: nodeLinks
      }
      
      const updateNodeInTree = (node: MindMapNode): MindMapNode => {
        if (node.id === editingNode.id) {
          return updatedNode
        }
        return { ...node, children: node.children.map(updateNodeInTree) }
      }
      
      const updatedMap = {
        ...currentMap,
        centerNode: updateNodeInTree(currentMap.centerNode),
        updatedAt: new Date()
      }
      
      setCurrentMap(updatedMap)
      setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
      setShowNodeEditor(false)
      setEditingNode(null)
      
      // Auto-save tetikle
      triggerAutoSave(updatedMap)
      
      toast.success('Dal güncellendi!')
    } catch (error) {
      console.error('Dal güncellenemedi:', error)
      toast.error('Dal güncellenemedi')
    }
  }, [editingNode, currentMap, nodeText, nodeColor, nodeIcon, nodeFontSize, nodeNotes, nodeShape, nodeFontWeight, nodeShadow, nodeGradient, nodeBorderWidth, nodeBorderColor, nodeOpacity, nodeLinks])

  // Delete node
  const deleteNode = useCallback(async (nodeToDelete: MindMapNode) => {
    if (!currentMap || nodeToDelete.id === 'center') return
    
    // Take snapshot before making changes
    takeSnapshot(currentMap)

    try {
      const removeNodeFromTree = (node: MindMapNode): MindMapNode => {
        return {
          ...node,
          children: node.children
            .filter(child => child.id !== nodeToDelete.id)
            .map(removeNodeFromTree)
        }
      }

      const updatedMap = {
        ...currentMap,
        centerNode: removeNodeFromTree(currentMap.centerNode),
        updatedAt: new Date()
      }

      setCurrentMap(updatedMap)
      setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
      
      // Auto-save tetikle
      triggerAutoSave(updatedMap)
      
      toast.success('Dal silindi!')
    } catch (error) {
      console.error('Dal silinemedi:', error)
      toast.error('Dal silinemedi')
    }
  }, [currentMap])

  // Handle node dragging
  const handleNodeMouseDown = useCallback((e: React.MouseEvent, node: MindMapNode) => {
    e.stopPropagation()
    
    // Önce node'u seç
    setSelectedNode(node)
    
    // Sonra sürükleme işlemini başlat
    setIsDraggingNode(true)
    setDraggedNode(node)
    const rect = (e.target as SVGElement).getBoundingClientRect()
    setDragOffset({
      x: e.clientX - node.x * zoom - pan.x,
      y: e.clientY - node.y * zoom - pan.y
    })
  }, [zoom, pan])

  const handleNodeMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDraggingNode && draggedNode && currentMap) {
      let newX = (e.clientX - dragOffset.x - pan.x) / zoom
      let newY = (e.clientY - dragOffset.y - pan.y) / zoom
      
      // Sayfa sınırları kontrolü
      const currentPageSize = pageSize === 'Custom' 
        ? { width: customPageWidth, height: customPageHeight }
        : pageSizes[pageSize]
      
      const nodeWidth = Math.max(80, draggedNode.text.length * (draggedNode.fontSize * 0.6) + 20)
      const nodeHeight = Math.max(40, draggedNode.fontSize + 20)
      
      // Node'un sayfa sınırları içinde kalmasını sağla
      newX = Math.max(nodeWidth / 2, Math.min(newX, currentPageSize.width - nodeWidth / 2))
      newY = Math.max(nodeHeight / 2, Math.min(newY, currentPageSize.height - nodeHeight / 2))
      
      const updateNodePosition = (node: MindMapNode): MindMapNode => {
        if (node.id === draggedNode.id) {
          return { ...node, x: newX, y: newY }
        }
        return { ...node, children: node.children.map(updateNodePosition) }
      }
      
      const updatedMap = {
        ...currentMap,
        centerNode: updateNodePosition(currentMap.centerNode),
        updatedAt: new Date()
      }
      
      setCurrentMap(updatedMap)
    }
  }, [isDraggingNode, draggedNode, currentMap, dragOffset, pan, zoom])

  const handleNodeMouseUp = useCallback(() => {
    if (isDraggingNode && draggedNode && currentMap) {
      setMindMaps(prev => prev.map(map => map.id === currentMap.id ? currentMap : map))
      // Auto-save tetikle
      triggerAutoSave(currentMap)
    }
    setIsDraggingNode(false)
    setDraggedNode(null)
  }, [isDraggingNode, draggedNode, currentMap, triggerAutoSave])

  // Render node recursively
  const renderNode = useCallback((node: MindMapNode, level: number = 0) => {
    const IconComponent = node.icon ? availableIcons[node.icon as keyof typeof availableIcons] : null
    const isRectangle = node.shape === 'rectangle'
    const nodeWidth = Math.max(80, node.text.length * (node.fontSize * 0.6) + 20)
    const nodeHeight = Math.max(40, node.fontSize + 20)
    
    return (
      <g key={node.id}>
        {/* Node connections */}
        {node.children.map(child => (
          <line
            key={`line-${node.id}-${child.id}`}
            x1={node.x}
            y1={node.y}
            x2={child.x}
            y2={child.y}
            stroke={node.color}
            strokeWidth={Math.max(1, 4 - level)}
            opacity={0.6}
          />
        ))}
        
        {/* Node shape */}
        {isRectangle ? (
          <rect
            x={node.x - nodeWidth / 2}
            y={node.y - nodeHeight / 2}
            width={nodeWidth}
            height={nodeHeight}
            rx={8}
            ry={8}
            fill={node.color}
            stroke={node.borderColor || "white"}
            strokeWidth={node.borderWidth || 2}
            opacity={node.opacity || 1}
            filter={node.shadow ? 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' : 'none'}
            className="cursor-move hover:opacity-80 transition-opacity"
            data-node-id={node.id}
            onMouseDown={(e) => handleNodeMouseDown(e, node)}
            onTouchStart={(e) => handleNodeTouchStart(e, node)}
            onClick={(e) => {
              e.stopPropagation();
              if (e.detail >= 2) {
                editNode(node);
              } else {
                setSelectedNode(node);
              }
            }}
          />
        ) : (
          <circle
            cx={node.x}
            cy={node.y}
            r={Math.max(20, node.fontSize + 10)}
            fill={node.color}
            stroke={node.borderColor || "white"}
            strokeWidth={node.borderWidth || 2}
            opacity={node.opacity || 1}
            filter={node.shadow ? 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' : 'none'}
            className="cursor-move hover:opacity-80 transition-opacity"
            data-node-id={node.id}
            onMouseDown={(e) => handleNodeMouseDown(e, node)}
            onTouchStart={(e) => handleNodeTouchStart(e, node)}
            onClick={(e) => {
              e.stopPropagation();
              if (e.detail >= 2) {
                editNode(node);
              } else {
                setSelectedNode(node);
              }
            }}
          />
        )}
        
        {/* Node icon */}
        {IconComponent && (
          <foreignObject
            x={isRectangle ? (node.x - nodeWidth / 2 + 6) : (node.x - Math.max(20, node.fontSize + 10) + 6)}
            y={isRectangle ? (node.y - nodeHeight / 2 + 6) : (node.y - Math.max(20, node.fontSize + 10) + 6)}
            width={16}
            height={16}
            className="pointer-events-none"
          >
            <IconComponent className="w-4 h-4" />
          </foreignObject>
        )}
        
        {/* Node text */}
        <text
          x={node.x}
          y={node.y + node.fontSize / 3}
          textAnchor="middle"
          fontSize={node.fontSize}
          fill="white"
          fontWeight={node.fontWeight || 'normal'}
          className="select-none cursor-move"
          data-node-id={node.id}
          onMouseDown={(e) => handleNodeMouseDown(e, node)}
          onTouchStart={(e) => handleNodeTouchStart(e, node)}
          onClick={(e) => {
            e.stopPropagation();
            if (e.detail >= 2) {
              editNode(node);
            } else {
              setSelectedNode(node);
            }
          }}
        >
          {node.text}
        </text>
        
        {/* Add button */}
        {selectedNode?.id === node.id && (
          <g>
            <circle
              cx={node.x + (isRectangle ? nodeWidth / 2 + 20 : 40)}
              cy={node.y}
              r={12}
              fill="#10B981"
              stroke="white"
              strokeWidth={2}
              className="cursor-pointer hover:opacity-80"
              onClick={() => addNode(node, 0)}
            />
            <foreignObject
              x={node.x + (isRectangle ? nodeWidth / 2 + 14 : 34)}
              y={node.y - 6}
              width={12}
              height={12}
              className="pointer-events-none"
            >
              <PlusIcon className="w-3 h-3 text-white" />
            </foreignObject>
          </g>
        )}
        
        {/* Render children */}
        {node.isExpanded && node.children.map(child => renderNode(child, level + 1))}
      </g>
    )
  }, [selectedNode, addNode, handleNodeMouseDown])

  // Handle canvas pan
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Allow panning unless clicking on a node or interactive element
    const target = e.target as HTMLElement
    const isNode = target.closest('[data-node-id]')
    const isButton = target.closest('button')
    const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA'
    
    if (!isNode && !isButton && !isInput) {
      setIsDragging(true)
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y })
    }
  }, [pan])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Mouse wheel to zoom only when Ctrl/Cmd is pressed; otherwise allow natural page scroll
  const handleWheel = useCallback((e: React.WheelEvent) => {
    // If Ctrl (Windows/Linux) or Cmd (macOS) is not pressed, do not intercept; let the page scroll
    if (!(e.ctrlKey || e.metaKey)) {
      return
    }

    e.preventDefault()
    const svg = svgRef.current
    if (!svg) return

    const rect = svg.getBoundingClientRect()
    const cursorX = e.clientX - rect.left
    const cursorY = e.clientY - rect.top

    // Smooth zoom factor
    const factor = Math.exp(-e.deltaY * 0.001)
    const nextZoom = Math.min(2, Math.max(0.5, zoom * factor))
    const scaleRatio = nextZoom / zoom

    const nextPan = {
      x: cursorX - (cursorX - pan.x) * scaleRatio,
      y: cursorY - (cursorY - pan.y) * scaleRatio
    }

    setZoom(nextZoom)
    setPan(nextPan)
  }, [zoom, pan])

  // Dokunmatik cihazlar için pan (canvas) handler'ları
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    const target = e.target as Element
    const isNode = target.closest('[data-node-id]')
    const isButton = (target as HTMLElement).closest('button')
    const tag = (target as HTMLElement).tagName?.toUpperCase()
    const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'

    // Tek parmakla sayfa kaydırmayı engelleme; yalnızca çoklu dokunuşta canvas pan başlat
    if (!isNode && !isButton && !isInput && touch) {
      if (e.touches.length > 1) {
        e.preventDefault()
        setIsDragging(true)
        setDragStart({ x: touch.clientX - pan.x, y: touch.clientY - pan.y })
      }
    }
  }, [pan])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    
    // Düğüm sürüklenirken sayfa kaydırmasını engelle
    if (isDraggingNode) {
      e.preventDefault()
      return
    }
    
    // Yalnızca çoklu dokunuşla sürükleme aktifken pan uygula
    if (isDragging && touch && e.touches.length > 1) {
      e.preventDefault()
      setPan({ x: touch.clientX - dragStart.x, y: touch.clientY - dragStart.y })
    }
  }, [isDragging, dragStart, isDraggingNode])

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Ekrana sığdır (fit-to-screen): Harita görünümüne geçildiğinde ve pencere boyutu değiştiğinde
  useEffect(() => {
    if (!currentMap) return
    const el = canvasRef.current
    if (!el) return

    const computeFit = () => {
      const cw = el.clientWidth
      const ch = el.clientHeight
      if (cw <= 0 || ch <= 0) return

      const currentPageSize = pageSize === 'Custom'
        ? { width: customPageWidth, height: customPageHeight }
        : pageSizes[pageSize]

      const scale = Math.min(
        Math.max(0.5, cw / currentPageSize.width),
        Math.max(0.5, ch / currentPageSize.height)
      )

      const nextZoom = Math.min(2, Math.max(0.5, scale))
      const nextPan = {
        x: (cw - currentPageSize.width * nextZoom) / 2,
        y: (ch - currentPageSize.height * nextZoom) / 2,
      }

      setZoom(nextZoom)
      setPan(nextPan)
    }

    // İlk çalıştırma ve resize'ta yeniden hesapla
    computeFit()
    window.addEventListener('resize', computeFit)
    return () => window.removeEventListener('resize', computeFit)
  }, [currentMap?.id, pageSize, customPageWidth, customPageHeight])

  // Dokunmatik cihazlar için düğüm sürükleme handler'ları
  const handleNodeTouchStart = useCallback((e: React.TouchEvent, node: MindMapNode) => {
    e.stopPropagation()
    e.preventDefault()
    const touch = e.touches[0]
    if (!touch) return

    setSelectedNode(node)
    setIsDraggingNode(true)
    setDraggedNode(node)
    if (currentMap) takeSnapshot(currentMap)
    setDragOffset({
      x: touch.clientX - node.x * zoom - pan.x,
      y: touch.clientY - node.y * zoom - pan.y
    })
  }, [zoom, pan])

  const handleNodeTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    if (isDraggingNode && draggedNode && currentMap && touch) {
      e.preventDefault()
      let newX = (touch.clientX - dragOffset.x - pan.x) / zoom
      let newY = (touch.clientY - dragOffset.y - pan.y) / zoom

      const currentPageSize = pageSize === 'Custom'
        ? { width: customPageWidth, height: customPageHeight }
        : pageSizes[pageSize]

      const nodeWidth = Math.max(80, draggedNode.text.length * (draggedNode.fontSize * 0.6) + 20)
      const nodeHeight = Math.max(40, draggedNode.fontSize + 20)

      newX = Math.max(nodeWidth / 2, Math.min(newX, currentPageSize.width - nodeWidth / 2))
      newY = Math.max(nodeHeight / 2, Math.min(newY, currentPageSize.height - nodeHeight / 2))

      const updateNodePosition = (node: MindMapNode): MindMapNode => {
        if (node.id === draggedNode.id) {
          return { ...node, x: newX, y: newY }
        }
        return { ...node, children: node.children.map(updateNodePosition) }
      }

      // takeSnapshot(currentMap) // moved to touch start to avoid performance issues during continuous move
      const updatedMap = {
        ...currentMap,
        centerNode: updateNodePosition(currentMap.centerNode),
        updatedAt: new Date()
      }

      setCurrentMap(updatedMap)
    }
  }, [isDraggingNode, draggedNode, currentMap, dragOffset, pan, zoom, pageSize, customPageWidth, customPageHeight])

  const handleNodeTouchEnd = useCallback(() => {
    if (isDraggingNode && draggedNode && currentMap) {
      setMindMaps(prev => prev.map(map => map.id === currentMap.id ? currentMap : map))
      triggerAutoSave(currentMap)
    }
    setIsDraggingNode(false)
    setDraggedNode(null)
  }, [isDraggingNode, draggedNode, currentMap, triggerAutoSave])

  // Dokunmatik sürüklemede (node taşırken) sayfa kaydırmasını kilitle
  useEffect(() => {
    if (!isDraggingNode) return

    const preventScroll = (e: TouchEvent) => {
      e.preventDefault()
    }

    // Global touchmove'u engelle (passive:false gerekli)
    window.addEventListener('touchmove', preventScroll, { passive: false })

    // Body scroll'u ve overscroll zincirini kapat
    const prevOverflow = document.body.style.overflow
    const prevTouchAction = (document.body.style as any).touchAction
    const prevOverscroll = (document.body.style as any).overscrollBehavior

    document.body.style.overflow = 'hidden'
    ;(document.body.style as any).touchAction = 'none'
    ;(document.body.style as any).overscrollBehavior = 'contain'

    return () => {
      window.removeEventListener('touchmove', preventScroll)
      document.body.style.overflow = prevOverflow
      ;(document.body.style as any).touchAction = prevTouchAction
      ;(document.body.style as any).overscrollBehavior = prevOverscroll
    }
  }, [isDraggingNode])

  // Zoom controls
  const zoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2))
  const zoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5))
  const resetView = () => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }

  // Keyboard shortcut actions object (moved here to avoid TDZ on zoomIn/zoomOut)
  const keyboardActions = useMemo(() => ({
    save: () => {
      if (currentMap) {
        manualSave()
      }
    },
    undo: () => {
      undo()
    },
    redo: () => {
      redo()
    },
    zoomIn: () => {
      zoomIn()
    },
    zoomOut: () => {
      zoomOut()
    },
    resetView: () => {
      resetView()
    },
    addNode: () => {
      if (selectedNode) {
        addNode(selectedNode)
      } else {
        toast.error('Düğüm eklemek için önce bir düğüm seçin')
      }
    },
    deleteNode: () => {
      if (selectedNode && selectedNode.id !== 'center') {
        deleteNode(selectedNode)
      } else {
        toast.error('Merkez düğüm silinemez')
      }
    },
    copy: copyNode,
    paste: pasteNode,
    selectAll: () => {
      toggleAllNodes(true)
      toast.success('Tüm düğümler genişletildi')
    },
    toggleGrid: toggleGrid,
    fullscreen: () => {
      toggleFullscreen()
    },
    help: () => {
      toast.success('Klavye kısayolları: Ctrl+S (Kaydet), Ctrl+Z (Geri Al), Ctrl+Y (İleri Al), Ctrl++ (Yakınlaştır), Ctrl+- (Uzaklaştır), Ctrl+0 (Sıfırla), Tab (Düğüm Ekle), Delete (Düğüm Sil), Ctrl+C (Kopyala), Ctrl+V (Yapıştır), Ctrl+A (Tümünü Seç), Ctrl+G (Izgara), F11 (Tam Ekran), F1 (Yardım)')
    }
  }), [currentMap, selectedNode, undo, redo, zoomIn, zoomOut, resetView, addNode, deleteNode, copyNode, pasteNode, toggleAllNodes, toggleGrid, toggleFullscreen, manualSave])

  // Register keyboard shortcuts
  const keyboardShortcuts = useMemo(() => getMindMapKeyboardShortcuts({
    onSave: keyboardActions.save,
    onUndo: keyboardActions.undo,
    onRedo: keyboardActions.redo,
    onZoomIn: keyboardActions.zoomIn,
    onZoomOut: keyboardActions.zoomOut,
    onResetView: keyboardActions.resetView,
    onAddNode: keyboardActions.addNode,
    onDeleteSelected: keyboardActions.deleteNode,
    onCopy: keyboardActions.copy,
    onPaste: keyboardActions.paste,
    onSelectAll: keyboardActions.selectAll,
    onToggleGrid: keyboardActions.toggleGrid,
    onToggleFullscreen: keyboardActions.fullscreen,
    onShowHelp: keyboardActions.help
  }), [keyboardActions])

  useKeyboardShortcuts({
    shortcuts: keyboardShortcuts,
    enabled: !showNodeEditor && !showThemeSelector && !showBackgroundSelector && !showCustomSizeModal
  })

  // Helper function to filter nodes within page bounds
  const filterNodesInBounds = (node: MindMapNode, bounds: { width: number; height: number }): MindMapNode | null => {
    // Check if node is within bounds
    if (node.x < 0 || node.x > bounds.width || node.y < 0 || node.y > bounds.height) {
      return null
    }
    
    // Filter children recursively
    const filteredChildren = node.children
      .map(child => filterNodesInBounds(child, bounds))
      .filter(child => child !== null) as MindMapNode[]
    
    return {
      ...node,
      children: filteredChildren
    }
  }

  // Export functions
  const exportAsPNG = useCallback(async (options: { transparent?: boolean } = {}) => {
    if (!currentMap) return
    
    try {
      // Seçilen sayfa boyutunu al
      const selectedPageSize = currentMap.pageSize || pageSize
      let exportWidth, exportHeight
      
      if (selectedPageSize === 'Custom') {
        exportWidth = currentMap.customWidth || customPageWidth
        exportHeight = currentMap.customHeight || customPageHeight
      } else {
        exportWidth = pageSizes[selectedPageSize].width
        exportHeight = pageSizes[selectedPageSize].height
      }
      
      // Sayfa sınırları içindeki node'ları filtrele
      const filteredCenterNode = filterNodesInBounds(currentMap.centerNode, { width: exportWidth, height: exportHeight })
      
      if (!filteredCenterNode) {
        toast.error('Sayfa sınırları içinde görüntülenecek içerik bulunamadı!')
        return
      }
      
      // Geçici SVG oluştur
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      tempSvg.setAttribute('width', exportWidth.toString())
      tempSvg.setAttribute('height', exportHeight.toString())
      tempSvg.setAttribute('viewBox', `0 0 ${exportWidth} ${exportHeight}`)
      tempSvg.style.backgroundColor = 'transparent'
      
      // Filtered node'ları render et
      const renderFilteredNode = (node: MindMapNode, level: number = 0): string => {
        const IconComponent = node.icon ? availableIcons[node.icon as keyof typeof availableIcons] : null
        const isRectangle = node.shape === 'rectangle'
        const nodeWidth = Math.max(80, node.text.length * (node.fontSize * 0.6) + 20)
        const nodeHeight = Math.max(40, node.fontSize + 20)
        
        let svg = ''
        
        // Node connections
        node.children.forEach(child => {
          svg += `<line x1="${node.x}" y1="${node.y}" x2="${child.x}" y2="${child.y}" stroke="${node.color}" stroke-width="${Math.max(1, 4 - level)}" opacity="0.6" />`
        })
        
        // Node shape
        if (isRectangle) {
          svg += `<rect x="${node.x - nodeWidth / 2}" y="${node.y - nodeHeight / 2}" width="${nodeWidth}" height="${nodeHeight}" rx="8" ry="8" fill="${node.color}" stroke="${node.borderColor || 'white'}" stroke-width="${node.borderWidth || 2}" opacity="${node.opacity || 1}" />`
        } else {
          svg += `<circle cx="${node.x}" cy="${node.y}" r="${Math.max(20, node.fontSize + 10)}" fill="${node.color}" stroke="${node.borderColor || 'white'}" stroke-width="${node.borderWidth || 2}" opacity="${node.opacity || 1}" />`
        }
        
        // Node text
        svg += `<text x="${node.x}" y="${node.y + node.fontSize / 3}" text-anchor="middle" font-size="${node.fontSize}" fill="white" font-weight="${node.fontWeight || 'normal'}">${node.text}</text>`
        
        // Render children
        node.children.forEach(child => {
          svg += renderFilteredNode(child, level + 1)
        })
        
        return svg
      }
      
      tempSvg.innerHTML = renderFilteredNode(filteredCenterNode)
      
      // Geçici container oluştur
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '-9999px'
      tempContainer.style.width = exportWidth + 'px'
      tempContainer.style.height = exportHeight + 'px'
      
      // Arka plan stillerini uygula
      if (options.transparent) {
        tempContainer.style.backgroundColor = 'transparent'
      } else {
        tempContainer.style.backgroundColor = (currentMap.backgroundType === 'solid' && (!currentMap.backgroundColor || currentMap.backgroundColor.toUpperCase() === '#FFFFFF'))
          ? (isDark ? '#0f172a' : '#FFFFFF')
          : (currentMap.backgroundColor || (isDark ? '#0f172a' : '#FFFFFF'))
        
        // Pattern arka planları uygula
        if (currentMap.backgroundType && currentMap.backgroundType !== 'solid') {
          const bg = backgroundOptions[currentMap.backgroundType as keyof typeof backgroundOptions]
          if ('pattern' in bg) {
            tempContainer.style.backgroundImage = bg.pattern
            tempContainer.style.backgroundSize = bg.size
            tempContainer.style.backgroundRepeat = 'repeat'
          }
        }
      }
      
      tempContainer.appendChild(tempSvg)
      document.body.appendChild(tempContainer)

      const html2canvas = (await import('html2canvas')).default
      
      const canvasElement = await html2canvas(tempContainer, {
        backgroundColor: options.transparent ? null : ((currentMap.backgroundType === 'solid' && (!currentMap.backgroundColor || currentMap.backgroundColor.toUpperCase() === '#FFFFFF'))
          ? (isDark ? '#0f172a' : '#FFFFFF')
          : (currentMap.backgroundColor || (isDark ? '#0f172a' : '#FFFFFF'))),
        scale: exportQuality,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: exportWidth,
        height: exportHeight
      })

      // Geçici container'ı temizle
      document.body.removeChild(tempContainer)

      // Download link oluştur
      const link = document.createElement('a')
      link.download = `${currentMap.title}${options.transparent ? '_seffaf' : ''}.png`
      link.href = canvasElement.toDataURL('image/png')
      link.click()
      
      toast.success(`PNG olarak dışa aktarıldı! ${options.transparent ? '(Şeffaf arka plan)' : ''}`)
    } catch (error) {
      console.error('PNG export error:', error)
      toast.error('PNG dışa aktarımında hata oluştu!')
    }
  }, [currentMap, exportQuality])

  const exportAsPDF = useCallback(async (options: { transparent?: boolean; format?: 'A4' | 'A3' | 'Letter' } = {}) => {
    if (!currentMap) return
    
    try {
      // Seçilen sayfa boyutunu al (PDF format parametresi öncelikli)
      const selectedFormat = options.format || exportFormat
      const selectedPageSize = currentMap.pageSize || pageSize
      let exportWidth, exportHeight
      
      // PDF format parametresi varsa onu kullan, yoksa mevcut sayfa boyutunu kullan
      if (options.format) {
        exportWidth = pageSizes[selectedFormat].width
        exportHeight = pageSizes[selectedFormat].height
      } else if (selectedPageSize === 'Custom') {
        exportWidth = currentMap.customWidth || customPageWidth
        exportHeight = currentMap.customHeight || customPageHeight
      } else {
        exportWidth = pageSizes[selectedPageSize].width
        exportHeight = pageSizes[selectedPageSize].height
      }
      
      // Sayfa sınırları içindeki node'ları filtrele
      const filteredCenterNode = filterNodesInBounds(currentMap.centerNode, { width: exportWidth, height: exportHeight })
      
      if (!filteredCenterNode) {
        toast.error('Sayfa sınırları içinde görüntülenecek içerik bulunamadı!')
        return
      }
      
      // Geçici SVG oluştur
      const tempSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      tempSvg.setAttribute('width', exportWidth.toString())
      tempSvg.setAttribute('height', exportHeight.toString())
      tempSvg.setAttribute('viewBox', `0 0 ${exportWidth} ${exportHeight}`)
      tempSvg.style.backgroundColor = 'transparent'
      
      // Filtered node'ları render et (aynı renderFilteredNode fonksiyonunu kullan)
      const renderFilteredNode = (node: MindMapNode, level: number = 0): string => {
        const IconComponent = node.icon ? availableIcons[node.icon as keyof typeof availableIcons] : null
        const isRectangle = node.shape === 'rectangle'
        const nodeWidth = Math.max(80, node.text.length * (node.fontSize * 0.6) + 20)
        const nodeHeight = Math.max(40, node.fontSize + 20)
        
        let svg = ''
        
        // Node connections
        node.children.forEach(child => {
          svg += `<line x1="${node.x}" y1="${node.y}" x2="${child.x}" y2="${child.y}" stroke="${node.color}" stroke-width="${Math.max(1, 4 - level)}" opacity="0.6" />`
        })
        
        // Node shape
        if (isRectangle) {
          svg += `<rect x="${node.x - nodeWidth / 2}" y="${node.y - nodeHeight / 2}" width="${nodeWidth}" height="${nodeHeight}" rx="8" ry="8" fill="${node.color}" stroke="${node.borderColor || 'white'}" stroke-width="${node.borderWidth || 2}" opacity="${node.opacity || 1}" />`
        } else {
          svg += `<circle cx="${node.x}" cy="${node.y}" r="${Math.max(20, node.fontSize + 10)}" fill="${node.color}" stroke="${node.borderColor || 'white'}" stroke-width="${node.borderWidth || 2}" opacity="${node.opacity || 1}" />`
        }
        
        // Node text
        svg += `<text x="${node.x}" y="${node.y + node.fontSize / 3}" text-anchor="middle" font-size="${node.fontSize}" fill="white" font-weight="${node.fontWeight || 'normal'}">${node.text}</text>`
        
        // Render children
        node.children.forEach(child => {
          svg += renderFilteredNode(child, level + 1)
        })
        
        return svg
      }
      
      tempSvg.innerHTML = renderFilteredNode(filteredCenterNode)
      
      // Geçici container oluştur
      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '-9999px'
      tempContainer.style.width = exportWidth + 'px'
      tempContainer.style.height = exportHeight + 'px'
      
      // Arka plan stillerini uygula
      if (options.transparent) {
        tempContainer.style.backgroundColor = 'transparent'
      } else {
        tempContainer.style.backgroundColor = (currentMap.backgroundType === 'solid' && (!currentMap.backgroundColor || currentMap.backgroundColor.toUpperCase() === '#FFFFFF'))
          ? (isDark ? '#0f172a' : '#FFFFFF')
          : (currentMap.backgroundColor || (isDark ? '#0f172a' : '#FFFFFF'))
        
        // Pattern arka planları uygula
        if (currentMap.backgroundType && currentMap.backgroundType !== 'solid') {
          const bg = backgroundOptions[currentMap.backgroundType as keyof typeof backgroundOptions]
          if ('pattern' in bg) {
            tempContainer.style.backgroundImage = bg.pattern
            tempContainer.style.backgroundSize = bg.size
            tempContainer.style.backgroundRepeat = 'repeat'
          }
        }
      }
      
      tempContainer.appendChild(tempSvg)
      document.body.appendChild(tempContainer)

      const [html2canvas, jsPDF] = await Promise.all([
        import('html2canvas').then(m => m.default),
        import('jspdf').then(m => m.jsPDF)
      ])
      
      const canvasElement = await html2canvas(tempContainer, {
        backgroundColor: options.transparent ? null : ((currentMap.backgroundType === 'solid' && (!currentMap.backgroundColor || currentMap.backgroundColor.toUpperCase() === '#FFFFFF'))
          ? (isDark ? '#0f172a' : '#FFFFFF')
          : (currentMap.backgroundColor || (isDark ? '#0f172a' : '#FFFFFF'))),
        scale: exportQuality,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: exportWidth,
        height: exportHeight
      })

      // Geçici container'ı temizle
      document.body.removeChild(tempContainer)

      const imgData = canvasElement.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: options.format || exportFormat
      })

      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvasElement.width
      const imgHeight = canvasElement.height
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
      const imgX = (pdfWidth - imgWidth * ratio) / 2
      const imgY = (pdfHeight - imgHeight * ratio) / 2

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
      pdf.save(`${currentMap.title}${options.transparent ? '_seffaf' : ''}.pdf`)
      
      toast.success(`PDF olarak dışa aktarıldı! ${options.transparent ? '(Şeffaf arka plan)' : ''}`)
    } catch (error) {
      console.error('PDF export error:', error)
      toast.error('PDF dışa aktarımında hata oluştu!')
    }
  }, [currentMap, exportQuality])

  const exportAsJSON = useCallback(() => {
    if (!currentMap) return
    
    const dataStr = JSON.stringify(currentMap, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${currentMap.title}.json`
    link.click()
    URL.revokeObjectURL(url)
    toast.success('JSON olarak dışa aktarıldı!')
  }, [currentMap])

  return (
    <div ref={containerRef} className={`min-h-screen flex flex-col overflow-hidden ${isFullscreen ? 'bg-white dark:bg-gray-900' : 'bg-transparent'}`}>
      {/* Header */}
      <div className="hidden">
        <div className={`flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2 ${isFullscreen ? 'bg-white dark:bg-gray-900' : 'bg-white/30 dark:bg-gray-900/20 backdrop-blur-md'}`}>
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <BookOpenIcon className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
              <span className="hidden sm:inline">Zihin Haritaları</span>
              <span className="sm:hidden">Haritalar</span>
            </h1>
            {currentMap && (
              <span className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 truncate max-w-32 sm:max-w-none">
                → {currentMap.title}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            {!currentMap ? (
              <motion.button
                onClick={() => setIsCreating(true)}
                className="bg-purple-500 hover:bg-purple-600 text-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm sm:text-base"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Yeni Harita</span>
                <span className="sm:hidden">Yeni</span>
              </motion.button>
            ) : (
              <>
                {/* Zoom Controls */}
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                  <button
                    onClick={zoomOut}
                    className="p-1 sm:p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <MagnifyingGlassMinusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                  <span className="px-1 sm:px-2 text-xs sm:text-sm font-medium">{Math.round(zoom * 100)}%</span>
                  <button
                    onClick={zoomIn}
                    className="p-1 sm:p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <MagnifyingGlassPlusIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                  <button
                    onClick={resetView}
                    className="p-1 sm:p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                  >
                    <ArrowsPointingOutIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
                
                {/* Save Controls */}
                <div className="flex items-center gap-2">
                  {/* Manual Save Button */}
                  <button
                    onClick={manualSave}
                    disabled={isSaving || !hasUnsavedChanges}
                    className={`p-1 sm:p-2 rounded-lg transition-colors flex items-center gap-1 ${
                      isSaving || !hasUnsavedChanges
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                        : 'bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800 text-blue-600 dark:text-blue-400'
                    }`}
                  >
                    <DocumentArrowUpIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline text-xs">
                      {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
                    </span>
                  </button>
                  
                  {/* Save Status Indicator */}
                  <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                    {isSaving ? (
                      <>
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                        <span className="hidden sm:inline">Kaydediliyor...</span>
                      </>
                    ) : hasUnsavedChanges ? (
                      <>
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="hidden sm:inline">Kaydedilmemiş değişiklikler</span>
                      </>
                    ) : lastSaved ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="hidden sm:inline">
                          Son kayıt: {lastSaved.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </>
                    ) : null}
                  </div>
                  
                  {/* Auto-save Toggle */}
                  <button
                    onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
                    className={`p-1 sm:p-2 rounded-lg transition-colors ${
                      autoSaveEnabled
                        ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}
                    title={autoSaveEnabled ? 'Otomatik kaydetme açık' : 'Otomatik kaydetme kapalı'}
                  >
                    <span className="text-xs font-medium">
                      {autoSaveEnabled ? 'AUTO' : 'MAN'}
                    </span>
                  </button>
                </div>
                
                {/* Page Size Selector */}
                <div className="relative">
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value as 'A4' | 'A3' | 'Letter' | 'Custom')}
                    className="p-1 sm:p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-xs sm:text-sm border-none outline-none appearance-none pr-6"
                  >
                    <option value="A4">A4</option>
                    <option value="A3">A3</option>
                    <option value="Letter">Letter</option>
                    <option value="Custom">Özel</option>
                  </select>
                  <ChevronDownIcon className="w-3 h-3 absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500" />
                </div>
                
                {/* Page Bounds Toggle */}
                <button
                  onClick={() => setShowPageBounds(!showPageBounds)}
                  className={`p-1 sm:p-2 rounded-lg transition-colors ${
                    showPageBounds
                      ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                  title={showPageBounds ? 'Sayfa sınırlarını gizle' : 'Sayfa sınırlarını göster'}
                >
                  <Square3Stack3DIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                {/* Theme Selector */}
                <button
                  onClick={() => setShowThemeSelector(true)}
                  className="p-1 sm:p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <SwatchIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                {/* Export Options */}
                <button
                  onClick={() => setShowExportOptions(true)}
                  className="p-1 sm:p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                
                {/* Fullscreen Toggle */}
                <button
                  onClick={toggleFullscreen}
                  className="p-1 sm:p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  title={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : (
                    <ArrowsPointingOutIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </button>
                
                {/* Back to Maps */}
                <button
                  onClick={async () => {
                    // Tam ekrandan çık
                    try {
                      if (document.fullscreenElement) {
                        await document.exitFullscreen()
                      }
                      setIsFullscreen(false)
                    } catch (error) {
                      console.log('Tam ekrandan çıkış hatası:', error)
                      setIsFullscreen(false)
                    }
                    setCurrentMap(null)
                  }}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg transition-colors text-sm sm:text-base flex items-center gap-1"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Haritalar</span>
                  <span className="sm:hidden">Geri</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Left Floating Toolbar (only when a map is open) */}
      {currentMap && (
        <div className={`fixed z-50 ${isFullscreen ? 'top-4' : 'top-16'} ${!isFullscreen && sidebarOpen ? 'left-[18rem]' : 'left-2'} transition-all duration-300`}>
          <div className="w-12 rounded-full bg-white/40 dark:bg-gray-900/40 border border-white/50 dark:border-gray-700/50 backdrop-blur-md shadow-xl flex flex-col items-center py-2 gap-1">
            {/* Save Status Indicator */}
            <div
              className={`w-2 h-2 rounded-full ${isSaving ? 'bg-yellow-500 animate-pulse' : hasUnsavedChanges ? 'bg-red-500' : lastSaved ? 'bg-green-500' : 'bg-gray-400'}`}
              title={isSaving ? 'Kaydediliyor...' : hasUnsavedChanges ? 'Kaydedilmemiş değişiklikler' : lastSaved ? `Son kayıt: ${lastSaved.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}` : 'Kayıt bilgisi yok'}
            />

            {/* Manual Save */}
            <button onClick={manualSave} disabled={isSaving || !hasUnsavedChanges} title={isSaving ? 'Kaydediliyor...' : (hasUnsavedChanges ? 'Kaydet' : 'Kaydedildi')} className={`p-2 rounded-full transition ${isSaving || !hasUnsavedChanges ? 'opacity-50 cursor-not-allowed bg-white/30 dark:bg-gray-800/30' : 'hover:bg-white/60 dark:hover:bg-gray-800/60'}`}>
              <DocumentArrowUpIcon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            </button>

            {/* Auto-save Toggle */}
            <button
              onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
              className={`p-2 rounded-full transition ${autoSaveEnabled ? 'bg-green-100/70 dark:bg-green-900/40' : 'hover:bg-white/60 dark:hover:bg-gray-800/60'}`}
              title={autoSaveEnabled ? 'Otomatik kaydetme açık' : 'Otomatik kaydetme kapalı'}
            >
              <span className={`text-[10px] font-semibold ${autoSaveEnabled ? 'text-green-700 dark:text-green-300' : 'text-gray-700 dark:text-gray-300'}`}>{autoSaveEnabled ? 'A' : 'M'}</span>
            </button>

            <div className="my-1 h-px w-8 bg-gray-200/60 dark:bg-gray-700/60" />

            {/* Zoom Controls */}
            <button onClick={zoomIn} title="Yakınlaştır" className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
              <MagnifyingGlassPlusIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
            </button>
            <button onClick={zoomOut} title="Uzaklaştır" className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
              <MagnifyingGlassMinusIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
            </button>
            <button onClick={resetView} title="Görünümü Sıfırla" className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
              <ArrowsPointingOutIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
            </button>

            <div className="my-1 h-px w-8 bg-gray-200/60 dark:bg-gray-700/60" />

            {/* Page Size Picker */}
            <div className="relative">
              <button onClick={() => setShowPageSizePicker((v) => !v)} title="Sayfa boyutu" className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
                <DocumentTextIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
              </button>
              {showPageSizePicker && (
                <div className="absolute left-14 top-0 z-50 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-2 w-40">
                  <select
                    value={pageSize}
                    onChange={(e) => { handlePageSizeChange(e.target.value as 'A4' | 'A3' | 'Letter' | 'Custom'); setShowPageSizePicker(false) }}
                    className="w-full p-1 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white focus:outline-none"
                  >
                    <option value="A4">A4</option>
                    <option value="A3">A3</option>
                    <option value="Letter">Letter</option>
                    <option value="Custom">Özel</option>
                  </select>
                </div>
              )}
            </div>

            {/* Page Bounds Toggle */}
            <button
              onClick={() => setShowPageBounds(!showPageBounds)}
              className={`p-2 rounded-full transition ${showPageBounds ? 'bg-blue-100/70 dark:bg-blue-900/40' : 'hover:bg-white/60 dark:hover:bg-gray-800/60'}`}
              title={showPageBounds ? 'Sayfa sınırlarını gizle' : 'Sayfa sınırlarını göster'}
            >
              <Square3Stack3DIcon className={`w-4 h-4 ${showPageBounds ? 'text-blue-600 dark:text-blue-400' : 'text-gray-800 dark:text-gray-200'}`} />
            </button>

            {/* Theme Selector */}
            <button onClick={() => setShowThemeSelector(true)} title="Tema" className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
              <SwatchIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
            </button>

            <div className="my-1 h-px w-8 bg-gray-200/60 dark:bg-gray-700/60" />

            {/* Export */}
            <button onClick={() => setShowExportOptions(true)} title="Dışa Aktar" className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
              <DocumentArrowDownIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
            </button>

            {/* Fullscreen Toggle */}
            <button onClick={toggleFullscreen} title={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'} className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
              {isFullscreen ? (
                <ArrowsPointingInIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
              ) : (
                <ArrowsPointingOutIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
              )}
            </button>

            {/* Back to Maps */}
            <button onClick={() => setCurrentMap(null)} title="Haritalara Dön" className="p-2 rounded-full hover:bg-white/60 dark:hover:bg-gray-800/60 transition">
              <ArrowLeftIcon className="w-4 h-4 text-gray-800 dark:text-gray-200" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`flex-1 h-full relative min-h-0 pt-0 sm:pt-0 ${currentMap ? 'overflow-hidden' : 'overflow-auto'}`}>
        {!currentMap ? (
          /* Mind Maps List */
          <div className="p-4 sm:p-6">
            {/* Modern Header */}
            <div className="mb-8">
              <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-md rounded-3xl border border-white/50 dark:border-gray-700/50 shadow-2xl p-6 md:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                      Zihin Haritaları
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                      Fikirlerinizi görselleştirin ve organize edin
                    </p>
                  </div>
                  <button
                    onClick={() => setIsCreating(true)}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-3 rounded-2xl font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                  >
                    <PlusIcon className="w-5 h-5" />
                    <span className="hidden sm:inline">Yeni Harita</span>
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              {mindMaps.map(map => (
                <motion.div
                    key={map.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white/85 dark:bg-gray-800/85 backdrop-blur-md rounded-2xl p-4 sm:p-6 border-2 border-gray-200/70 dark:border-gray-700/70 shadow-md hover:shadow-2xl hover:bg-white/95 dark:hover:bg-gray-800/95 transition-all duration-300 cursor-pointer group"
                    onClick={() => setCurrentMap(map)}
                  >
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {map.title}
                      </h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      themes[map.theme as keyof typeof themes] ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {themes[map.theme as keyof typeof themes]?.name || 'Varsayılan'}
                    </span>
                  </div>
                  
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                      Son güncelleme: {map.updatedAt.toLocaleDateString('tr-TR')}
                    </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpenIcon className="w-4 h-4 text-gray-400" />
                      <span className="text-xs sm:text-sm text-gray-500">
                        {/* Count total nodes */}
                        {(() => {
                          const countNodes = (node: MindMapNode): number => {
                            return 1 + node.children.reduce((sum, child) => sum + countNodes(child), 0)
                          }
                          return countNodes(map.centerNode)
                        })()} dal
                      </span>
                    </div>
                    
                    <button
                      onClick={async (e) => {
                        e.stopPropagation()
                        try {
                          await mindMapService.deleteMindMap(map.id)
                          setMindMaps(prev => prev.filter(m => m.id !== map.id))
                          toast.success('Harita silindi!')
                        } catch (error) {
                          console.error('Harita silinemedi:', error)
                          toast.error('Harita silinemedi')
                        }
                      }}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
              
              {/* Add New Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-purple-50/80 to-pink-50/80 dark:from-purple-900/30 dark:to-pink-900/30 backdrop-blur-md border-2 border-dashed border-purple-300/60 dark:border-purple-600/60 rounded-2xl p-4 sm:p-6 flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:from-purple-100/80 hover:to-pink-100/80 dark:hover:from-purple-800/40 dark:hover:to-pink-800/40 transition-all duration-300 min-h-[120px] sm:min-h-[140px] group"
                onClick={() => setIsCreating(true)}
              >
                <PlusIcon className="w-8 h-8 sm:w-12 sm:h-12 text-purple-500 dark:text-purple-400 mb-2 group-hover:scale-110 transition-transform duration-200" />
                <span className="text-sm sm:text-base text-purple-700 dark:text-purple-300 font-medium text-center">
                  <span className="hidden sm:inline">Yeni Harita Oluştur</span>
                  <span className="sm:hidden">Yeni Harita</span>
                </span>
              </motion.div>
            </div>
          </div>
        ) : (
          /* Mind Map Canvas */
          <div 
            ref={canvasRef}
            className="mind-map-canvas w-full h-full relative overflow-visible cursor-grab active:cursor-grabbing touch-pan-y select-none" 
            style={{ backgroundClip: 'padding-box', 
              touchAction: isDraggingNode ? 'none' : 'pan-y', 
              minWidth: '100%',
              minHeight: '100%',
              backgroundColor: (currentMap.backgroundType === 'solid' && (!currentMap.backgroundColor || currentMap.backgroundColor.toUpperCase() === '#FFFFFF'))
                ? (isDark ? '#0f172a' : '#FFFFFF')
                : (currentMap.backgroundColor || (isDark ? '#0f172a' : '#FFFFFF')),
              backgroundImage: currentMap.backgroundType && currentMap.backgroundType !== 'solid' 
                ? (() => {
                    const type = currentMap.backgroundType
                    const color = isDark ? '#334155' : '#CBD5E1'
                    if (type === 'dotted') return `radial-gradient(circle, ${color} 1px, transparent 1px)`
                    if (type === 'grid') return `linear-gradient(${color} 1px, transparent 1px), linear-gradient(90deg, ${color} 1px, transparent 1px)`
                    if (type === 'lines') return `linear-gradient(90deg, transparent 24px, ${color} 25px)`
                    return undefined
                  })()
                : undefined,
              backgroundSize: currentMap.backgroundType && currentMap.backgroundType !== 'solid' 
                ? (() => {
                    const type = currentMap.backgroundType
                    if (type === 'dotted' || type === 'grid') return '20px 20px'
                    if (type === 'lines') return '25px 25px'
                    return undefined
                  })()
                : undefined,
              backgroundRepeat: currentMap.backgroundType && currentMap.backgroundType !== 'solid' ? 'repeat' : undefined
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={(e) => {
              handleMouseMove(e)
              handleNodeMouseMove(e)
            }}
            onMouseUp={() => {
              handleMouseUp()
              handleNodeMouseUp()
            }}
            onMouseLeave={() => {
              handleMouseUp()
              handleNodeMouseUp()
            }}
            onTouchStart={(e) => {
              // If touch starts on a node element, prevent page scroll
              const target = e.target as HTMLElement
              if (target && target.getAttribute && target.getAttribute('data-node-id')) {
                e.preventDefault()
              }
              handleTouchStart(e)
            }}
            onTouchMove={(e) => {
              handleTouchMove(e)
              handleNodeTouchMove(e)
            }}
            onTouchEnd={() => {
              handleTouchEnd()
              handleNodeTouchEnd()
            }}
            onTouchCancel={() => {
              handleTouchEnd()
              handleNodeTouchEnd()
            }}
          >
            <svg
              ref={svgRef}
              className="w-full h-full"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                minWidth: `${(pageSize === 'Custom' ? customPageWidth : pageSizes[pageSize].width) * Math.max(1, zoom)}px`,
                minHeight: `${(pageSize === 'Custom' ? customPageHeight : pageSizes[pageSize].height) * Math.max(1, zoom)}px`
              }}
            >
              {/* Sayfa sınırları çerçevesi */}
              {showPageBounds && (() => {
                const currentPageSize = pageSize === 'Custom' 
                  ? { width: customPageWidth, height: customPageHeight }
                  : pageSizes[pageSize]
                return (
                  <rect
                    x={0}
                    y={0}
                    width={currentPageSize.width}
                    height={currentPageSize.height}
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    strokeDasharray="10,5"
                    opacity={0.5}
                  />
                )
              })()}
              
              {renderNode(currentMap.centerNode)}
            </svg>
            
            {/* Node Info Panel */}
            {selectedNode && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="fixed right-3 sm:right-4 top-28 sm:top-32 z-50 bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 shadow-lg border border-gray-200 dark:border-gray-700 w-72 sm:w-80 max-w-[92vw] max-h-[calc(100vh-8rem)] overflow-y-auto"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {selectedNode.text}
                  </h3>
                  <button
                    onClick={() => setSelectedNode(null)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    ×
                  </button>
                </div>
                
                <div className="space-y-2">
                  <button
                    onClick={() => editNode(selectedNode)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                    Düzenle
                  </button>
                  
                  <button
                    onClick={() => addNode(selectedNode)}
                    className="w-full flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Alt Dal Ekle
                  </button>
                  
                  {selectedNode.id !== 'center' && (
                    <button
                      onClick={() => {
                        deleteNode(selectedNode)
                        setSelectedNode(null)
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Sil
                    </button>
                  )}
                </div>
                
                {selectedNode.notes && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Notlar
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                      {selectedNode.notes}
                    </p>
                  </div>
                )}
                
                {/* Linkler */}
                {selectedNode.links && selectedNode.links.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bağlantılar
                    </h4>
                    <div className="space-y-2">
                      {selectedNode.links.map((link, index) => (
                        <a
                          key={index}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          <span className="truncate">{link.title || link.url}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        )}
      </div>

      {/* Create New Mind Map Modal */}
      <AnimatePresence>
        {isCreating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsCreating(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto no-scrollbar"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Yeni Zihin Haritası
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Harita Başlığı
                  </label>
                  <input
                    type="text"
                    value={newMapTitle}
                    onChange={(e) => setNewMapTitle(e.target.value)}
                    placeholder="Örn: Asitler ve Bazlar"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tema (LGS Dersleri)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(themes).map(([key, theme]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedTheme(key)}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          selectedTheme === key
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex gap-1">
                            {theme.colors.slice(0, 3).map((color, i) => (
                              <div
                                key={i}
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {theme.name}
                        </span>
                      </button>
                    ))}
                  </div>
                  
                  {/* LGS Şablonları */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hazır LGS Şablonları
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      <button
                        onClick={() => createTemplateMap('matematik-cebirsel')}
                        className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📐</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">Matematik</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Cebirsel İfadeler</div>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => createTemplateMap('fen-asitler-bazlar')}
                        className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 dark:hover:border-green-600 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🧪</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">Fen Bilimleri</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Asitler ve Bazlar</div>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => createTemplateMap('turkce-sozcuk-anlam')}
                        className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-600 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">📚</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">Türkçe</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Sözcükte Anlam</div>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => createTemplateMap('tarih-osmanli')}
                        className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-300 dark:hover:border-orange-600 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🏛️</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">Tarih</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Osmanlı Devleti</div>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => createTemplateMap('cografya-iklim')}
                        className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 dark:hover:border-green-600 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🌍</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">Coğrafya</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">İklim Tipleri</div>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => createTemplateMap('ingilizce-present-tense')}
                        className="p-3 text-left rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-300 dark:hover:border-purple-600 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-lg">🇬🇧</span>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">İngilizce</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">Present Tense</div>
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Notlar */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5" />
                    Notlar
                  </h3>
                  <textarea
                    value={nodeNotes}
                    onChange={(e) => setNodeNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                    placeholder="Bu dal hakkında ek notlar ve açıklamalar yazabilirsiniz..."
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setIsCreating(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={createNewMindMap}
                  disabled={!newMapTitle.trim()}
                  className="flex-1 px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  Oluştur
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Node Editor Modal */}
      <AnimatePresence>
        {showNodeEditor && editingNode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowNodeEditor(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-lg max-h-[80vh] overflow-hidden overflow-y-auto overflow-x-hidden overscroll-contain no-scrollbar"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Dal Düzenle
                </h2>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Canlı Önizleme</span>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* Temel Bilgiler */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <PencilIcon className="w-5 h-5" />
                    Temel Bilgiler
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Dal Metni
                      </label>
                      <input
                        type="text"
                        value={nodeText}
                        onChange={(e) => setNodeText(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        placeholder="Dal başlığını girin..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Notlar
                      </label>
                      <textarea
                        value={nodeNotes}
                        onChange={(e) => setNodeNotes(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all"
                        placeholder="Bu dal hakkında ek notlar ve açıklamalar..."
                      />
                    </div>
                  </div>
                </div>

                {/* Görsel Tasarım */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <SwatchIcon className="w-5 h-5" />
                    Görsel Tasarım
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Renk Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Renk Paleti
                      </label>
                      
                      {/* Renk Kategorileri */}
                      <div className="mb-3">
                        <div className="flex gap-1 flex-wrap">
                          {Object.entries(colorPalette).map(([category, colors]) => (
                            <button
                              key={category}
                              onClick={() => setSelectedColorCategory(category as keyof typeof colorPalette)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                                selectedColorCategory === category 
                                  ? 'bg-blue-500 text-white shadow-md' 
                                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500'
                              }`}
                            >
                              {category === 'reds' ? 'Kırmızı' :
                               category === 'blues' ? 'Mavi' :
                               category === 'greens' ? 'Yeşil' :
                               category === 'purples' ? 'Mor' :
                               category === 'oranges' ? 'Turuncu' :
                               category === 'pinks' ? 'Pembe' :
                               category === 'yellows' ? 'Sarı' :
                               category === 'grays' ? 'Gri' :
                               category === 'teals' ? 'Turkuaz' :
                               category === 'indigos' ? 'İndigo' : category}
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Seçili Kategorinin Renkleri */}
                      <div className="grid grid-cols-8 gap-2 mb-3">
                        {colorPalette[selectedColorCategory].map(color => (
                          <button
                            key={color}
                            onClick={() => setNodeColor(color)}
                            className={`w-10 h-10 rounded-xl border-3 transition-all hover:scale-110 ${
                              nodeColor === color ? 'border-gray-900 dark:border-white shadow-lg' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      
                      {/* Özel Renk Seçici */}
                      <div className="flex gap-3 items-center p-3 bg-white dark:bg-gray-600 rounded-lg">
                        <input
                          type="color"
                          value={customNodeColor || nodeColor}
                          onChange={(e) => {
                            setCustomNodeColor(e.target.value)
                            setNodeColor(e.target.value)
                          }}
                          className="w-10 h-10 rounded-lg border-2 border-gray-300 dark:border-gray-500 cursor-pointer"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Özel Renk</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">İstediğiniz rengi seçin</p>
                        </div>
                      </div>
                    </div>
                
                    {/* İkon Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        İkon
                      </label>
                      <div className="grid grid-cols-6 gap-2">
                        {Object.keys(availableIcons).map(icon => (
                          <button
                            key={icon}
                            onClick={() => setNodeIcon(icon)}
                            className={`p-3 rounded-xl border-2 text-xl transition-all hover:scale-105 ${
                              nodeIcon === icon
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                            }`}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Şekil Seçimi */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Dal Şekli
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setNodeShape('rectangle')}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                            nodeShape === 'rectangle' 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md' 
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <div className="w-8 h-6 bg-current rounded opacity-30"></div>
                          <span className="text-sm font-medium">Dikdörtgen</span>
                        </button>
                        <button
                          onClick={() => setNodeShape('circle')}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                            nodeShape === 'circle' 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md' 
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <div className="w-7 h-7 bg-current rounded-full opacity-30"></div>
                          <span className="text-sm font-medium">Daire</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tipografi */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <span className="text-xl">Aa</span>
                    Tipografi
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Yazı Boyutu */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Yazı Boyutu: <span className="text-blue-600 dark:text-blue-400 font-semibold">{nodeFontSize}px</span>
                      </label>
                      <div className="relative">
                        <input
                          type="range"
                          min="10"
                          max="32"
                          value={nodeFontSize}
                          onChange={(e) => setNodeFontSize(Number(e.target.value))}
                          className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>10px</span>
                          <span>32px</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Yazı Kalınlığı */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Yazı Kalınlığı
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setNodeFontWeight('normal')}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            nodeFontWeight === 'normal' 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md' 
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <span className="font-normal">Normal</span>
                        </button>
                        <button
                          onClick={() => setNodeFontWeight('bold')}
                          className={`p-3 rounded-xl border-2 transition-all ${
                            nodeFontWeight === 'bold' 
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md' 
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <span className="font-bold">Kalın</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Efektler */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <SparklesIcon className="w-5 h-5" />
                    Efektler
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Gölge Efekti */}
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-600 rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Gölge Efekti</span>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Dala gölge efekti ekler</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={nodeShadow}
                          onChange={(e) => setNodeShadow(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                
                    {/* Kenarlık Ayarları */}
                    <div className="p-3 bg-white dark:bg-gray-600 rounded-lg">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">Kenarlık</span>
                      <div className="grid grid-cols-3 gap-4 items-end">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">Kalınlık: <span className="text-blue-600 dark:text-blue-400 font-semibold">{nodeBorderWidth}px</span></label>
                          <input
                            type="range"
                            min="0"
                            max="8"
                            value={nodeBorderWidth}
                            onChange={(e) => setNodeBorderWidth(Number(e.target.value))}
                            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">Renk</label>
                          <input
                            type="color"
                            value={nodeBorderColor}
                            onChange={(e) => setNodeBorderColor(e.target.value)}
                            className="w-full h-10 rounded-lg border-2 border-gray-300 dark:border-gray-500 cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Şeffaflık */}
                    <div className="p-3 bg-white dark:bg-gray-600 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Şeffaflık: <span className="text-blue-600 dark:text-blue-400 font-semibold">{Math.round(nodeOpacity * 100)}%</span>
                      </label>
                      <div className="relative">
                        <input
                          type="range"
                          min="0.1"
                          max="1"
                          step="0.1"
                          value={nodeOpacity}
                          onChange={(e) => setNodeOpacity(Number(e.target.value))}
                          className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>10%</span>
                          <span>100%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Link Yönetimi */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <LinkIcon className="w-5 h-5" />
                    Bağlantılar
                  </h3>
                  
                  {/* Mevcut Linkler */}
                  {nodeLinks.length > 0 && (
                    <div className="mb-4 space-y-3">
                      {nodeLinks.map((link, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500">
                          <div className="flex-shrink-0">
                            <LinkIcon className="w-5 h-5 text-blue-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{link.title}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{link.url}</div>
                          </div>
                          <button
                            onClick={() => setNodeLinks(nodeLinks.filter((_, i) => i !== index))}
                            className="flex-shrink-0 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Yeni Link Ekleme */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto no-scrollbar">
                      <input
                        type="text"
                        value={newLinkTitle}
                        onChange={(e) => setNewLinkTitle(e.target.value)}
                        placeholder="Link başlığı (örn: Wikipedia)"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                      <input
                        type="url"
                        value={newLinkUrl}
                        onChange={(e) => setNewLinkUrl(e.target.value)}
                        placeholder="https://example.com"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <button
                      onClick={() => {
                        if (newLinkTitle.trim() && newLinkUrl.trim()) {
                          setNodeLinks([...nodeLinks, { title: newLinkTitle.trim(), url: newLinkUrl.trim() }])
                          setNewLinkTitle('')
                          setNewLinkUrl('')
                        }
                      }}
                      disabled={!newLinkTitle.trim() || !newLinkUrl.trim()}
                      className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl transition-all font-medium flex items-center justify-center gap-2"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Bağlantı Ekle
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <button
                  onClick={() => setShowNodeEditor(false)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all font-medium"
                >
                  İptal
                </button>
                <button
                  onClick={saveNodeChanges}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl"
                >
                  Değişiklikleri Kaydet
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Theme Selector Modal */}
      <AnimatePresence>
        {showThemeSelector && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowThemeSelector(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto no-scrollbar"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Tema Seç
              </h2>
              
              {/* Tema Seçimi */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Renk Teması
                </h3>
                <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto no-scrollbar">
                  {Object.entries(themes).map(([key, theme]) => (
                    <button
                      key={key}
                      onClick={() => {
                        if (currentMap) {
                          takeSnapshot(currentMap)
                          const updatedMap = { ...currentMap, theme: key, updatedAt: new Date() }
                          setCurrentMap(updatedMap)
                          setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
                        }
                        toast.success('Tema değiştirildi!')
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        currentMap?.theme === key
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                          {theme.colors.slice(0, 4).map((color, i) => (
                            <div
                              key={i}
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: color }}
                            />
                          ))}
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {theme.name}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Arka Plan Seçimi */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Arka Plan
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(backgroundOptions).map(([key, bg]) => (
                    <button
                      key={key}
                      onClick={() => {
                        if (currentMap) {
                          takeSnapshot(currentMap)
                          const updatedMap = { 
                            ...currentMap, 
                            backgroundType: key as any, 
                            backgroundColor: key === 'solid' ? '#FFFFFF' : '#FFFFFF',
                            updatedAt: new Date() 
                          }
                          setCurrentMap(updatedMap)
                          setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
                        }
                        toast.success('Arka plan değiştirildi!')
                      }}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        currentMap?.backgroundType === key
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                        {bg.name}
                      </div>
                      <div 
                        className="w-full h-8 rounded border border-gray-300 dark:border-gray-600"
                        style={{
                          backgroundColor: key === 'solid' ? '#FFFFFF' : '#FFFFFF',
                          backgroundImage: key !== 'solid' && 'pattern' in bg ? bg.pattern : undefined,
                          backgroundSize: key !== 'solid' && 'size' in bg ? bg.size : undefined,
                          backgroundRepeat: key !== 'solid' ? 'repeat' : undefined
                        }}
                      />
                    </button>
                  ))}
                </div>
                
                {/* Solid renk seçenekleri */}
                {currentMap?.backgroundType === 'solid' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Arka Plan Rengi
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {backgroundOptions.solid.colors.map(color => (
                        <button
                          key={color}
                          onClick={() => {
                            if (currentMap) {
                              takeSnapshot(currentMap)
                              const updatedMap = { ...currentMap, backgroundColor: color, updatedAt: new Date() }
                              setCurrentMap(updatedMap)
                              setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
                            }
                          }}
                          className={`w-8 h-8 rounded border-2 ${
                            currentMap?.backgroundColor === color 
                              ? 'border-purple-500' 
                              : 'border-gray-300 dark:border-gray-600'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                      <input
                        type="color"
                        value={currentMap?.backgroundColor || '#FFFFFF'}
                        onChange={(e) => {
                          if (currentMap) {
                            takeSnapshot(currentMap)
                            const updatedMap = { ...currentMap, backgroundColor: e.target.value, updatedAt: new Date() }
                            setCurrentMap(updatedMap)
                            setMindMaps(prev => prev.map(map => map.id === updatedMap.id ? updatedMap : map))
                          }
                        }}
                        className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setShowThemeSelector(false)}
                className="w-full mt-4 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Kapat
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Custom Page Size Modal */}
      <AnimatePresence>
        {showCustomSizeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCustomSizeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Özel Sayfa Boyutu
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Genişlik (piksel)
                  </label>
                  <input
                    type="number"
                    value={customPageWidth}
                    onChange={(e) => setCustomPageWidth(Number(e.target.value))}
                    min="400"
                    max="3000"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yükseklik (piksel)
                  </label>
                  <input
                    type="number"
                    value={customPageHeight}
                    onChange={(e) => setCustomPageHeight(Number(e.target.value))}
                    min="300"
                    max="3000"
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Mevcut boyut: {customPageWidth} × {customPageHeight} piksel
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    // Önceki seçime geri dön ve modalı kapat
                    setPageSize(prevPageSize)
                    setShowCustomSizeModal(false)
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={() => {
                    const width = Math.max(400, Math.min(3000, customPageWidth))
                    const height = Math.max(300, Math.min(3000, customPageHeight))
                    setCustomPageWidth(width)
                    setCustomPageHeight(height)

                    setPageSize('Custom')

                    if (currentMap) {
                      const updatedMap = {
                        ...currentMap,
                        pageSize: 'Custom' as const,
                        customWidth: width,
                        customHeight: height,
                        updatedAt: new Date()
                      }
                      setCurrentMap(updatedMap)
                      triggerAutoSave(updatedMap)
                    }

                    setShowCustomSizeModal(false)
                    toast.success(`Özel sayfa boyutu ayarlandı: ${width}×${height}px`)
                  }}
                  className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  Uygula
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Export Options Modal */}
      <AnimatePresence>
        {showExportOptions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowExportOptions(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                <DocumentArrowDownIcon className="w-6 h-6" />
                Dışa Aktar
              </h2>
              
              {/* Export Options */}
              <div className="space-y-4 mb-6">
                {/* Transparent Background Option */}
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Şeffaf Arka Plan
                  </label>
                  <button
                    onClick={() => setExportTransparent(!exportTransparent)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      exportTransparent ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        exportTransparent ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Quality Setting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kalite
                  </label>
                  <div className="flex gap-2">
                    {[1, 2, 3].map((quality) => (
                      <button
                        key={quality}
                        onClick={() => setExportQuality(quality as 1 | 2 | 3)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          exportQuality === quality
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {quality === 1 ? 'Düşük' : quality === 2 ? 'Orta' : 'Yüksek'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* PDF Format Setting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    PDF Formatı
                  </label>
                  <div className="flex gap-2">
                    {(['A4', 'A3', 'Letter'] as const).map((format) => (
                      <button
                        key={format}
                        onClick={() => setExportFormat(format)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          exportFormat === format
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {format}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Export Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    exportAsPNG({ transparent: exportTransparent })
                    setShowExportOptions(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <PhotoIcon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">PNG Resmi</div>
                    <div className="text-xs opacity-75">
                      {exportTransparent ? 'Şeffaf arka plan' : 'Normal arka plan'} • {exportQuality === 1 ? 'Düşük' : exportQuality === 2 ? 'Orta' : 'Yüksek'} kalite
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    exportAsPDF({ transparent: exportTransparent, format: exportFormat })
                    setShowExportOptions(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <DocumentArrowDownIcon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">PDF Dosyası</div>
                    <div className="text-xs opacity-75">
                      {exportFormat} format • {exportTransparent ? 'Şeffaf arka plan' : 'Normal arka plan'}
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={() => {
                    exportAsJSON()
                    setShowExportOptions(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                >
                  <DocumentArrowUpIcon className="w-5 h-5" />
                  <div className="text-left">
                    <div className="font-medium">JSON Verisi</div>
                    <div className="text-xs opacity-75">Zihin haritası verilerini kaydet</div>
                  </div>
                </button>
              </div>
              
              <button
                onClick={() => setShowExportOptions(false)}
                className="w-full mt-4 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default MindMaps